import React, {useEffect, useState, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";
import {Row, Col, Card, Button, Spinner, Modal} from "react-bootstrap";
import BootstrapDropdown from "react-bootstrap/Dropdown";
import moment from "moment";
import DateHelper from "../../../primer/DateHelper/DateHelper";
import DateBox from "../../../primer/DateBox";
import {getGreekCurrencyFormat, getDOYOptions} from "../../../../_helpers/commonFunctions";
import {
    setNewAccountingRecord,
    setF2ReportRequestData,
    setF2ReportResponseData,
    setF2ZoomLevel,
    setF2FormOptions,
    setF2ExportSource,
    setF2VisibleColumns,
    setTaxisScrapingState
} from "../../../../_reducers/DataPanel/Accounting/accountingSlice";
import {openNewAccountingRecord} from "../../../../_helpers/tabOpeners";
import {fetchAccountingSettings, postSubmittedVatDeclarations} from "../../../../_apis/api";
import {toast} from "react-toastify";
import {useTranslation} from "../../../../hooks/useTranslation";

const F2ColumnsContext = React.createContext({erp: true, myData: true, accounting: true});
const EXTENSION_STORE_URL = "https://chromewebstore.google.com/detail/inimchhmmahoignojckagbdbjhigglce";
const DEFAULT_TAXIS_CREDENTIALS = {
    username: "",
    password: "",
    isAccountant: false,
    accountantAfm: ""
};

const formatNumberOnly = (val) => {
    if (val === undefined || val === null || val === "" || isNaN(Number(val))) return "0,00";
    return Number(val).toLocaleString("el-GR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const TripleHeader = ({hideErp = false, borderTop = true}) => {
    const visibleColumns = React.useContext(F2ColumnsContext);
    const showErp = visibleColumns.erp && !hideErp;
    const showMyData = visibleColumns.myData;
    const showAccounting = visibleColumns.accounting;
    const visibleCount = (showErp ? 1 : 0) + (showMyData ? 1 : 0) + (showAccounting ? 1 : 0);
    const cellWidth = visibleCount === 0 ? "0%" : `${100 / visibleCount}%`;

    return (
        <div className="d-flex w-100 bg-white"
             style={{height: "16px", borderTop: borderTop ? "1px solid #000" : "none"}}>
            {showErp && (
                <div className="d-flex align-items-center justify-content-center border-right border-secondary px-0"
                     style={{width: cellWidth, minWidth: 0}}>
                    <span className="text-muted font-weight-bold"
                          style={{fontSize: "0.55rem", letterSpacing: "-0.5px"}}>ΕΜΠΟΡΙΚΟ</span>
                </div>
            )}
            {showMyData && (
                <div
                    className="d-flex align-items-center justify-content-center border-right border-secondary bg-light px-0"
                    style={{width: cellWidth, minWidth: 0}}>
                    <span className="text-primary font-weight-bold"
                          style={{fontSize: "0.55rem", letterSpacing: "-0.5px"}}>MYDATA</span>
                </div>
            )}
            {showAccounting && (
                <div className="d-flex align-items-center justify-content-center px-0"
                     style={{width: cellWidth, minWidth: 0}}>
                    <span className="text-muted font-weight-bold"
                          style={{fontSize: "0.55rem", letterSpacing: "-0.5px"}}>ΛΟΓΙΣΤΙΚΗ</span>
                </div>
            )}
        </div>
    );
};

const TripleCell = ({
                        erp = 0,
                        myData = 0,
                        accounting = 0,
                        code,
                        hasActions = false,
                        onActionClick = null,
                        hideErp = false
                    }) => {
    const textStyle = {fontSize: "0.54rem", fontWeight: "600", letterSpacing: "-0.5px"};
    const visibleColumns = React.useContext(F2ColumnsContext);
    const showErp = visibleColumns.erp && !hideErp;
    const showMyData = visibleColumns.myData;
    const showAccounting = visibleColumns.accounting;
    const visibleCount = (showErp ? 1 : 0) + (showMyData ? 1 : 0) + (showAccounting ? 1 : 0);
    const cellWidth = visibleCount === 0 ? "0%" : `${100 / visibleCount}%`;
    const isErpDiff = Math.abs(Number(erp) - Number(myData)) > 0.01;
    const isAccDiff = Math.abs(Number(accounting) - Number(myData)) > 0.01;

    const renderCellContent = (value, isMyData = false, source = null) => {
        const formatted = formatNumberOnly(value);
        const customStyle = isMyData ? {...textStyle, fontWeight: "bold"} : textStyle;
        const textColorClass = isMyData ? "text-primary" : "";

        const actionIcon = (hasActions && !isMyData && source && value > 0) ? (
            <i className="fa-solid fa-caret-down position-absolute text-primary"
               style={{right: "1px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: "0.65rem"}}
               onClick={(e) => {
                   e.stopPropagation();
                   if (onActionClick) onActionClick(source, code);
               }}
               title="Ανάλυση ανά κατηγορία ΦΠΑ"></i>
        ) : null;

        return (
            <div className="w-100 d-flex align-items-center justify-content-center position-relative px-0"
                 style={{height: "100%", minWidth: 0}}>
                <span className={`text-truncate w-100 text-center ${textColorClass}`} style={{
                    ...customStyle,
                    paddingRight: (hasActions && !isMyData) ? "10px" : "1px",
                    paddingLeft: "1px"
                }}>{formatted}</span>
                {actionIcon}
            </div>
        );
    };

    return (
        <div className="d-flex w-100 h-100 bg-white" style={{minHeight: "22px"}}>
            {showErp && (
                <div
                    className="px-0 py-1 border-right border-secondary d-flex align-items-center justify-content-center"
                    title={isErpDiff ? `Ασυμφωνία με myDATA! ${formatNumberOnly(erp)}` : formatNumberOnly(erp)}
                    style={{
                        width: cellWidth,
                        minWidth: 0,
                        overflow: "hidden",
                        position: "relative",
                        backgroundColor: isErpDiff ? "#f8d7da" : "transparent"
                    }}>
                    {renderCellContent(erp, false, "erp")}
                </div>
            )}
            {showMyData && (
                <div
                    className="px-0 py-1 border-right border-secondary d-flex align-items-center justify-content-center bg-light"
                    title={formatNumberOnly(myData)}
                    style={{width: cellWidth, minWidth: 0, overflow: "hidden", position: "relative"}}>
                    {renderCellContent(myData, true)}
                </div>
            )}
            {showAccounting && (
                <div className="px-0 py-1 d-flex align-items-center justify-content-center"
                     title={isAccDiff ? `Ασυμφωνία με myDATA! ${formatNumberOnly(accounting)}` : formatNumberOnly(accounting)}
                     style={{
                         width: cellWidth,
                         minWidth: 0,
                         overflow: "hidden",
                         position: "relative",
                         backgroundColor: isAccDiff ? "#f8d7da" : "transparent"
                     }}>
                    {renderCellContent(accounting, false, "accounting")}
                </div>
            )}
        </div>
    );
};

const TripleInputCell = ({
                             code,
                             erp = 0,
                             myData = 0,
                             accounting = 0,
                             hideErp = false,
                             f2FormOptions,
                             onValuesChange
                         }) => {
    const visibleColumns = React.useContext(F2ColumnsContext);
    const showErp = visibleColumns.erp && !hideErp;
    const showMyData = visibleColumns.myData;
    const showAccounting = visibleColumns.accounting;
    const visibleCount = (showErp ? 1 : 0) + (showMyData ? 1 : 0) + (showAccounting ? 1 : 0);
    const cellWidth = visibleCount === 0 ? "0%" : `${100 / visibleCount}%`;

    const handleChange = (source, value) => {
        if (onValuesChange) {
            onValuesChange({
                [`${code}_${source}`]: value
            });
        }
    };

    const getActualValue = (source) => {
        const manualVal = f2FormOptions?.[`${code}_${source}`];
        if (manualVal !== undefined && manualVal !== null && manualVal !== "") return manualVal;
        const defaultVal = source === 'erp' ? erp : source === 'myData' ? myData : accounting;
        return defaultVal === 0 ? "" : defaultVal;
    };

    const actualErpNum = Number(getActualValue("erp")) || 0;
    const actualMyDataNum = Number(getActualValue("myData")) || 0;
    const actualAccNum = Number(getActualValue("accounting")) || 0;
    const isErpDiff = Math.abs(actualErpNum - actualMyDataNum) > 0.01;
    const isAccDiff = Math.abs(actualAccNum - actualMyDataNum) > 0.01;

    const inputStyle = {
        width: "100%",
        height: "100%",
        border: "none",
        textAlign: "center",
        fontSize: "0.54rem",
        fontWeight: "600",
        letterSpacing: "-0.5px",
        backgroundColor: "transparent",
        outline: "none",
        padding: "0 1px"
    };

    const FormatInput = ({source, textColor}) => {
        const [isFocused, setIsFocused] = React.useState(false);
        const actualVal = getActualValue(source);
        const [tempVal, setTempVal] = React.useState(actualVal);

        useEffect(() => {
            if (!isFocused) setTempVal(actualVal);
        }, [actualVal, isFocused]);

        const handleInputChange = (e) => setTempVal(e.target.value.replace(/[^0-9.,]/g, ''));
        const handleBlur = (e) => {
            setIsFocused(false);
            let val = e.target.value;
            if (val === "") {
                handleChange(source, "");
                return;
            }
            val = val.replace(',', '.');
            const parsed = parseFloat(val);
            if (!isNaN(parsed)) {
                handleChange(source, parsed);
            } else {
                setTempVal(actualVal);
            }
        };

        const displayValue = isFocused ? tempVal : (actualVal === "" ? "" : formatNumberOnly(actualVal));

        return (
            <input
                type="text"
                className="f2-no-spinners text-truncate"
                style={{...inputStyle, color: textColor}}
                value={displayValue}
                onFocus={() => setIsFocused(true)}
                onBlur={handleBlur}
                onChange={handleInputChange}
                placeholder="0,00"
                title="Πληκτρολογήστε ποσό"
            />
        );
    };

    return (
        <div className="d-flex w-100 h-100 bg-white" style={{minHeight: "22px"}}>
            {showErp && (
                <div
                    className="px-0 py-0 border-right border-secondary d-flex align-items-center justify-content-center"
                    style={{
                        width: cellWidth,
                        minWidth: 0,
                        backgroundColor: isErpDiff ? "#f8d7da" : "transparent"
                    }}>
                    <FormatInput source="erp" textColor="inherit"/>
                </div>
            )}
            {showMyData && (
                <div
                    className="px-0 py-0 border-right border-secondary d-flex align-items-center justify-content-center bg-light"
                    style={{width: cellWidth, minWidth: 0}}>
                    <FormatInput source="myData" textColor="#76AEB7"/>
                </div>
            )}
            {showAccounting && (
                <div
                    className="px-0 py-0 d-flex align-items-center justify-content-center"
                    style={{
                        width: cellWidth,
                        minWidth: 0,
                        backgroundColor: isAccDiff ? "#f8d7da" : "transparent"
                    }}>
                    <FormatInput source="accounting" textColor="inherit"/>
                </div>
            )}
        </div>
    );
};

const CrossableNumber = ({number, isCrossed}) => (
    <div style={{
        position: "relative",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%"
    }}>
        <span>{number}</span>
        {isCrossed && (
            <span style={{
                position: "absolute",
                fontWeight: "normal",
                fontSize: "1.5rem",
                lineHeight: 1,
                color: "rgba(0, 0, 0, 0.35)"
            }}>X</span>
        )}
    </div>
);

const checkExtensionInstalled = (timeoutMs = 1200) => {
    return new Promise((resolve) => {
        const handleMessage = (event) => {
            if (event.source === window && event.data?.type === 'TAXIS_EXTENSION_PONG') {
                window.removeEventListener('message', handleMessage);
                clearTimeout(timer);
                resolve(true);
            }
        };

        window.addEventListener('message', handleMessage);
        window.postMessage({type: 'PING_TAXIS_EXTENSION'}, '*');

        const timer = setTimeout(() => {
            window.removeEventListener('message', handleMessage);
            resolve(false);
        }, timeoutMs);
    });
};

const isChromiumBrowser = () => {
    const nav = window.navigator;
    if (nav.userAgentData && Array.isArray(nav.userAgentData.brands)) {
        return nav.userAgentData.brands.some((b) =>
            ['Chromium', 'Google Chrome', 'Microsoft Edge', 'Opera', 'Brave'].includes(b.brand)
        );
    }

    const ua = nav.userAgent;
    const isChromium = /Chrome|Chromium|Edg|OPR/i.test(ua);
    const isExcluded = /Firefox|FxiOS/i.test(ua) || (/Safari/i.test(ua) && !/Chrome|Edg|OPR/i.test(ua));

    return isChromium && !isExcluded;
};

const calculateVatBalancesDynamic = (val401 = 0, val470 = 0, val480 = 0) => {
    const num401 = Number(val401) || 0;
    const num470 = Number(val470) || 0;
    const num480 = Number(val480) || 0;

    let res502 = 0;
    let res511 = 0;

    if (num480 > num401) {
        res502 = 0;
        res511 = +(num480 - num401).toFixed(2);
    } else if (num480 > 0) {
        res502 = +(num401 - num480).toFixed(2);
        res511 = 0;
    } else if (num470 > 0) {
        res502 = +(num401 + num470).toFixed(2);
        res511 = 0;
    } else {
        res502 = +num401.toFixed(2);
        res511 = 0;
    }

    return {val502: res502, val511: res511};
};

const AccountingIssueVatInformation = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const companyDetails = useSelector((state) => state.COMPANY_DATA.companyDetails);
    const requestData = useSelector((state) => state.ACCOUNTING_DATA.f2ReportRequestData);
    const responseData = useSelector((state) => state.ACCOUNTING_DATA.f2ReportResponseData);
    const zoomLevel = useSelector((state) => state.ACCOUNTING_DATA.f2ZoomLevel) || 1;
    const f2FormOptions = useSelector((state) => state.ACCOUNTING_DATA.f2FormOptions) || {
        declarationType: null,
        installment523: 1,
        noIbanAccount: false
    };
    const exportSource = useSelector((state) => state.ACCOUNTING_DATA.f2ExportSource) || "myData";
    const f2VisibleColumns = useSelector((state) => state.ACCOUNTING_DATA.f2VisibleColumns) || {
        erp: true,
        myData: true,
        accounting: true
    };
    const taxisCredentials = useSelector((state) => state.ACCOUNTING_DATA.taxisCredentials) || DEFAULT_TAXIS_CREDENTIALS;
    const handleToggleColumn = (colName) => {
        dispatch(setF2VisibleColumns({
            ...f2VisibleColumns,
            [colName]: !f2VisibleColumns[colName]
        }));
    };
    const TABS_DATA = useSelector((state) => state.TABS_REDUCER);
    const newAccountingRecord = useSelector((state) => state.ACCOUNTING_DATA.newAccountingRecord) || {};
    const [loading, setLoading] = useState(false);
    const [taxisLoading, setTaxisLoading] = useState(false);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [activeAnalysisData, setActiveAnalysisData] = useState({source: null, code: null});
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragScrollLeft, setDragScrollLeft] = useState(0);
    const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
    const [showExtensionModal, setShowExtensionModal] = useState(false);

    const prevCompanyIdRef = useRef(company?.id);
    const activeTaxisCompanyIdRef = useRef(null);
    const activeTaxisAfmRef = useRef(null);

    const resetTaxisState = () => {
        activeTaxisCompanyIdRef.current = null;
        activeTaxisAfmRef.current = null;
        setTaxisLoading(false);
        dispatch(setTaxisScrapingState({active: false, message: ""}));
    };

    useEffect(() => {
        if (!requestData || !requestData.date_from) {
            const prevMonth = moment().subtract(1, 'month');
            dispatch(setF2ReportRequestData({
                date_from: prevMonth.startOf('month').format("DD/MM/YYYY"),
                date_to: prevMonth.endOf('month').format("DD/MM/YYYY")
            }));
        }
    }, []);

    useEffect(() => {
        if (!company?.id) return;
        if (prevCompanyIdRef.current && prevCompanyIdRef.current !== company.id) {
            dispatch(setF2ReportResponseData(null));
            dispatch(setF2FormOptions({
                declarationType: null,
                installment523: 1,
                noIbanAccount: false,
                notes: ""
            }));
        }

        prevCompanyIdRef.current = company.id;

        dispatch(fetchAccountingSettings({
            company: company.id
        }));
    }, [company?.id, dispatch]);

    useEffect(() => {
        const checkExtension = (event) => {
            if (event.source !== window) return;
            if (event.data?.type === 'TAXIS_EXTENSION_READY' || event.data?.type === 'TAXIS_EXTENSION_PONG') {
                setIsExtensionInstalled(true);
            }
        };

        window.addEventListener('message', checkExtension);
        window.postMessage({type: 'PING_TAXIS_EXTENSION'}, '*');
        return () => window.removeEventListener('message', checkExtension);
    }, []);

    useEffect(() => {
        const handleExtensionMessages = (event) => {
            if (event.source !== window || !event.data) return;

            const {type, action} = event.data;
            const success = event.data.success ?? event.data.response?.success;
            const rawError = event.data.error || event.data.response?.error;
            const data = event.data.data ?? event.data.response?.data;

            if (type === 'TO_ERP_AUTOFILL_VAT_RESPONSE') {
                if (success === false || rawError) {
                    resetTaxisState();
                    toast.error(rawError || 'Αποτυχία εκκίνησης του Taxis Bridge.', {
                        autoClose: 10000
                    });
                }
                return;
            }

            if (type === 'TO_ERP_POST_FILL_SCRAPING_STARTED') {
                setTaxisLoading(true);
                dispatch(setTaxisScrapingState({
                    active: true,
                    message: "Σε εξέλιξη έλεγχος υποβολής και αυτόματη άντληση δήλωσης Φ2 από το Taxisnet..."
                }));
                toast.info('Έλεγχος υποβολής και αυτόματη άντληση δήλωσης από το Taxisnet...', {
                    autoClose: 4000
                });
                return;
            }

            if (type === 'TO_ERP_SCRAPE_VAT_RESPONSE') {
                if (activeTaxisCompanyIdRef.current && activeTaxisCompanyIdRef.current !== company?.id) {
                    resetTaxisState();
                    return;
                }

                if (success) {
                    if (!data) {
                        resetTaxisState();
                        toast.info('Δεν εντοπίστηκε υποβληθείσα δήλωση στο Taxisnet για την επιλεγμένη περίοδο.', {
                            autoClose: 5000
                        });
                        return;
                    }

                    const declarationsArray = Array.isArray(data) ? data : [data];
                    const scrapedAfm = declarationsArray[0]?.companyAfm?.trim();
                    const currentCompanyAfm = (company?.vatNumber || '').trim();

                    if (scrapedAfm && currentCompanyAfm && scrapedAfm !== currentCompanyAfm) {
                        resetTaxisState();
                        return;
                    }

                    const isAmending = declarationsArray[0]?.declarationType === 'AMENDING';
                    const typeLabel = isAmending ? 'Τροποποιητική δήλωση' : 'Αρχική δήλωση';

                    toast.info(`Εντοπίστηκε υποβληθείσα ${typeLabel} (Πρωτ: ${declarationsArray[0]?.protocolNumber || '-'}). Αποθήκευση...`, {
                        autoClose: 4000
                    });

                    const targetCompanyId = company?.id;
                    dispatch(postSubmittedVatDeclarations(
                        {company: targetCompanyId, declarations: declarationsArray},
                        setTaxisLoading,
                        () => {
                            resetTaxisState();
                            toast.success(`Η ${typeLabel} αποθηκεύτηκε επιτυχώς στο ERP!`);
                            fetchF2Data();
                        }
                    ));
                } else if (rawError) {
                    resetTaxisState();
                    toast.error(rawError || 'Απέτυχε η άντληση στοιχείων από το Taxisnet.', {
                        autoClose: 10000,
                        pauseOnHover: true
                    });
                }
                return;
            }

            if (action === 'AUTOFILL_COMPLETED' || type === 'TAXIS_AUTOFILL_COMPLETED') {
                resetTaxisState();
                const count = event.data.filledCount ?? event.data.response?.filledCount ?? 0;
                toast.success(`Η φόρμα συμπληρώθηκε (${count} πεδία). Ελέγξτε και πατήστε Υποβολή στο Taxisnet.`, {
                    autoClose: 7000,
                    pauseOnHover: true
                });
                return;
            }

            if (action === 'AUTOFILL_FAILED' || action === 'AUTOFILL_FAILED_AND_CLOSE') {
                resetTaxisState();
                toast.error(rawError || 'Η διαδικασία στο Taxisnet απέτυχε.', {
                    autoClose: 10000,
                    pauseOnHover: true
                });
            }

            if (type === 'TO_ERP_TAXIS_WINDOW_CLOSED') {
                resetScrapeState();
                toast.info('Η διαδικασία myAADE ακυρώθηκε (το παράθυρο έκλεισε).', {
                    autoClose: 4000
                });
            }
        };

        window.addEventListener('message', handleExtensionMessages);
        return () => window.removeEventListener('message', handleExtensionMessages);
    }, [company?.id, company?.vatNumber, dispatch, requestData]);

    const fetchF2Data = () => {
        setLoading(true);

        axios.post(`${process.env.REACT_APP_API_URL2}/report/vat-information-report`, {
            company: company?.id,
            date_from: requestData.date_from,
            date_to: requestData.date_to,
            grouped_per_day: "true",
            forExport: true,
            forceRefresh: true,
            calculateAccounting: "true"
        }, {
            headers: {"Content-Type": "application/json"}
        }).then((res) => {
            setLoading(false);
            if (res.data && (res.data.status === "200")) {
                dispatch(setF2ReportResponseData({
                    myData: res.data.totals || {},
                    accounting: res.data.accountingTotals || {},
                    erp: res.data.erpTotals || {}
                }));
            }
        }).catch((err) => {
            setLoading(false);
            console.error(err);
        });
    };

    const getCellData = (code) => {
        const dataObj = responseData || {};
        const myDataMap = dataObj.myData || dataObj;
        const accountingMap = dataObj.accounting || {};
        const erpMap = dataObj.erp || {};

        const getValByCode = (map, c) => {
            const val = map[`Vat${c}`] !== undefined ? map[`Vat${c}`] : map[c];
            return val ? Number(val) : 0;
        };

        const getEffective401 = (source, rawMap) => {
            const manual = f2FormOptions?.[`401_${source}`];
            if (manual !== undefined && manual !== null && manual !== "") return Number(manual);
            return getValByCode(rawMap, 401);
        };

        const eff401_erp = getEffective401('erp', erpMap);
        const eff401_myData = getEffective401('myData', myDataMap);
        const eff401_accounting = getEffective401('accounting', accountingMap);

        const eff470_erp = getValByCode(erpMap, 470);
        const eff480_erp = getValByCode(erpMap, 480);
        const eff470_myData = getValByCode(myDataMap, 470);
        const eff480_myData = getValByCode(myDataMap, 480);
        const eff470_acc = getValByCode(accountingMap, 470);
        const eff480_acc = getValByCode(accountingMap, 480);

        if (Number(code) === 502) {
            const calcErp = calculateVatBalancesDynamic(eff401_erp, eff470_erp, eff480_erp);
            const calcMyData = calculateVatBalancesDynamic(eff401_myData, eff470_myData, eff480_myData);
            const calcAcc = calculateVatBalancesDynamic(eff401_accounting, eff470_acc, eff480_acc);

            const manualErp = f2FormOptions?.['502_erp'];
            const manualMyData = f2FormOptions?.['502_myData'];
            const manualAcc = f2FormOptions?.['502_accounting'];

            return {
                erp: (manualErp !== undefined && manualErp !== "") ? Number(manualErp) : calcErp.val502,
                myData: (manualMyData !== undefined && manualMyData !== "") ? Number(manualMyData) : calcMyData.val502,
                accounting: (manualAcc !== undefined && manualAcc !== "") ? Number(manualAcc) : calcAcc.val502,
            };
        }

        if (Number(code) === 511) {
            const calcErp = calculateVatBalancesDynamic(eff401_erp, eff470_erp, eff480_erp);
            const calcMyData = calculateVatBalancesDynamic(eff401_myData, eff470_myData, eff480_myData);
            const calcAcc = calculateVatBalancesDynamic(eff401_accounting, eff470_acc, eff480_acc);

            return {
                erp: calcErp.val511,
                myData: calcMyData.val511,
                accounting: calcAcc.val511,
            };
        }

        if (Number(code) === 401) {
            return {
                erp: eff401_erp,
                myData: eff401_myData,
                accounting: eff401_accounting
            };
        }

        return {
            erp: getValByCode(erpMap, code),
            myData: getValByCode(myDataMap, code),
            accounting: getValByCode(accountingMap, code)
        };
    };

    const mFrom = moment(requestData?.date_from, "DD/MM/YYYY");
    const mTo = moment(requestData?.date_to, "DD/MM/YYYY");

    let selectedPeriod = {type: null, index: null};

    if (mFrom.isValid() && mTo.isValid()) {
        const monthsCount = (mTo.year() - mFrom.year()) * 12 + mTo.month() - mFrom.month() + 1;
        const startMonth = mFrom.month() + 1;

        if (monthsCount >= 12) {
            selectedPeriod = {type: 'year', index: 1};
        } else if (monthsCount >= 6) {
            selectedPeriod = {type: 'semester', index: startMonth <= 6 ? 1 : 2};
        } else if (monthsCount >= 3) {
            selectedPeriod = {type: 'quarter', index: Math.ceil(startMonth / 3)};
        } else {
            selectedPeriod = {type: 'month', index: startMonth};
        }
    }

    const selectedYear = mFrom.isValid() ? mFrom.format("YYYY") : "";

    const formStyle = {
        fontFamily: "'Arial', sans-serif",
        fontSize: "0.7rem",
        color: "#000",
        border: "2px solid #000",
        boxSizing: "border-box"
    };
    const grayBg = {backgroundColor: "#e2edf8"};
    const borderDark = "1px solid #000";

    const doyOptions = getDOYOptions();
    let doyLabel = companyDetails?.doy || "";
    let doyCode = "";

    if (companyDetails?.doy) {
        const matchedDoy = doyOptions.find(opt =>
            String(opt.value) === String(companyDetails.doy) ||
            String(opt.label) === String(companyDetails.doy)
        );

        if (matchedDoy) {
            doyLabel = matchedDoy.label;
            doyCode = String(matchedDoy.value).padStart(4, '0');
        }
    }

    const handleDeclarationTypeChange = (code) => {
        const newValue = f2FormOptions.declarationType === code ? null : code;
        dispatch(setF2FormOptions({declarationType: newValue}));
    };

    const handleInstallmentChange = (value) => {
        const newValue = f2FormOptions.installment523 === value ? null : value;
        dispatch(setF2FormOptions({installment523: newValue}));
    };

    const handleReturnReasonChange = (value) => {
        const newValue = f2FormOptions.returnReason507 === value ? null : value;
        dispatch(setF2FormOptions({returnReason507: newValue}));
    };

    const handleNoIbanAccountChange = () => {
        dispatch(setF2FormOptions({noIbanAccount: !f2FormOptions?.noIbanAccount}));
    };

    const handleOpenAnalysisModal = (source, code) => {
        const numericCode = Number(code);
        const baseCode = (numericCode >= 381 && numericCode <= 387) ? numericCode - 20 : numericCode;

        setActiveAnalysisData({source, code: numericCode, baseCode});
        setShowAnalysisModal(true);

        if (source === "accounting") {
            return;
        }

        setModalLoading(true);
        setModalData(null);

        axios.post(`${process.env.REACT_APP_API_URL2}/report/f2-expenses-analysis`, {
            company: company.id,
            date_from: requestData.date_from,
            date_to: requestData.date_to
        }, {
            headers: {"Content-Type": "application/json"}
        }).then((res) => {
            setModalLoading(false);
            if (res.data.status === "200") {
                if (source === "accounting") {
                    setModalData(res.data.data.accountingAnalysis);
                } else {
                    setModalData(res.data.data.erpAnalysis);
                }
            } else {
                toast.error(res.data.message || "Σφάλμα κατά την άντληση ανάλυσης.");
            }
        }).catch((err) => {
            setModalLoading(false);
            console.log(err);
            toast.error("Σφάλμα κατά την επικοινωνία με τον server.");
        });
    };

    const handleCloseAnalysisModal = () => {
        setShowAnalysisModal(false);
        setActiveAnalysisData({source: null, code: null});
    };

    const collectF2Payload = () => {
        const editableCodes = [
            301, 302, 303, 304, 305, 306, 308, 309, 310, 312, 313, 314, 315,
            361, 362, 363, 364, 365, 366,
            381, 382, 383, 384, 385, 386, 387,
            342, 345, 348, 349,
            401, 402, 403, 404, 407, 411, 422, 423, 430, 470, 480, 483,
            502, 503, 505, 511,
            906, 907, 908, 912
        ];

        const manualInputCodes = [401, 403, 404, 483, 502, 503, 505];
        const fields = {};

        editableCodes.forEach((c) => {
            let val;
            if (manualInputCodes.includes(c)) {
                const manualKey = `${c}_${exportSource}`;
                if (f2FormOptions && f2FormOptions[manualKey] !== undefined && f2FormOptions[manualKey] !== "") {
                    val = Number(f2FormOptions[manualKey]);
                } else {
                    const cell = getCellData(c);
                    val = Number(cell[exportSource] || 0);
                }
            } else if (c === 511) {
                const cell = getCellData(511);
                val = Number(cell[exportSource] || 0);
            } else {
                const cell = getCellData(c);
                val = Number(cell[exportSource] || 0);
            }

            if (!isNaN(val) && val !== 0) {
                fields[String(c)] = Number(val.toFixed(2));
            }
        });

        return fields;
    };

    const generateF2XML = (fields, options, period, companyAfm) => {
        const fromDateStr = period?.dateFrom
            ? moment(period.dateFrom, 'DD/MM/YYYY').format('YYYY-MM-DD')
            : moment().startOf('month').format('YYYY-MM-DD');
        const toDateStr = period?.dateTo
            ? moment(period.dateTo, 'DD/MM/YYYY').format('YYYY-MM-DD')
            : moment().endOf('month').format('YYYY-MM-DD');
        const targetYear = period?.year || moment(fromDateStr).year();
        const periodType = period?.type || (period?.quarter ? 'quarter' : 'month');
        const targetMonth = period?.month || (period?.quarter ? 0 : moment(fromDateStr).month() + 1);
        const targetQuarter = period?.quarter || 0;

        const finalFields = {...fields};

        const orderedFieldKeys = [
            '301', '302', '303', '304', '305', '306', '307', '308', '309', '310', '311',
            '331', '332', '333', '334', '335', '336', '337', '338', '339',
            '400', '401', '402', '403', '410', '411', '428', '430',
            '501', '502', '503',
            '507_CHECKBOXES',
            '511',
            '1000',
            '361', '362', '363', '364', '365', '366', '367',
            '381', '382', '383', '384', '385', '386', '387',
            '407', '422', '423',
            '342', '345', '348', '349', '312',
            '313', '314', '315',
            '470', '480', '483', '505', '508', '523',
            '404', '906', '907', '908', '912'
        ];

        let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
        xml += `<vatF2Ver2025Batch xmlns="http://www.taxisnet.gr/vat">\n`;
        xml += `  <vatF2Ver2025Document>\n`;
        xml += `    <overdue>0</overdue>\n`;
        xml += `    <f004>${targetYear}</f004>\n`;
        xml += `    <f005A>${fromDateStr}</f005A>\n`;
        xml += `    <f005B>${toDateStr}</f005B>\n`;

        if (periodType === 'quarter' || targetQuarter > 0) {
            xml += `    <f006B>${targetQuarter || period?.index || 1}</f006B>\n`;
        } else if (periodType === 'semester') {
            xml += `    <f006C>${period?.index || 1}</f006C>\n`;
        } else {
            xml += `    <f006A>${targetMonth || period?.index || 1}</f006A>\n`;
        }

        if (options?.declarationType === '007') xml += `    <f007>1</f007>\n`;
        if (options?.declarationType === '008') xml += `    <f008>1</f008>\n`;
        if (options?.declarationType === '009') xml += `    <f009>1</f009>\n`;
        if (companyAfm) {
            xml += `    <f104>${companyAfm.trim()}</f104>\n`;
        }
        if (options?.noIbanAccount) {
            xml += `    <f509>1</f509>\n`;
        }

        for (const code of orderedFieldKeys) {
            if (code === '507_CHECKBOXES') {
                if (options?.returnReason507) {
                    const rNum = options.returnReason507;
                    if (rNum >= 1 && rNum <= 6) {
                        xml += `    <f507C${rNum}>1</f507C${rNum}>\n`;
                    }
                }
                continue;
            }

            if (code === '523') {
                const instVal = options?.installment523 || 1;
                xml += `    <f523>${instVal}</f523>\n`;
                continue;
            }

            if (code === '1000') {
                if (options?.notes) {
                    const safeNotes = String(options.notes)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&apos;');
                    xml += `    <f1000>${safeNotes}</f1000>\n`;
                }
                continue;
            }

            if (finalFields && finalFields[code] !== undefined && finalFields[code] !== null && finalFields[code] !== '') {
                const numVal = Number(finalFields[code]);
                if (!isNaN(numVal) && numVal !== 0) {
                    xml += `    <f${code}>${numVal.toFixed(2)}</f${code}>\n`;
                }
            }
        }

        xml += `  </vatF2Ver2025Document>\n`;
        xml += `</vatF2Ver2025Batch>`;

        return xml;
    };

    const handleTriggerExtension = async (actionType = 'FORM') => {
        if (!isChromiumBrowser()) {
            toast.warning("Μη συμβατός περιηγητής. Για τη λειτουργία του Taxis Bridge απαιτείται Chrome, Edge, Brave ή Opera.", {
                autoClose: 7000,
                pauseOnHover: true
            });
            return;
        }

        if (!responseData) {
            toast.warning("Παρακαλώ εκτελέστε πρώτα «Υπολογισμός Φ2».");
            return;
        }

        let installed = isExtensionInstalled;
        if (!installed) {
            installed = await checkExtensionInstalled(800);
            if (installed) {
                setIsExtensionInstalled(true);
            }
        }

        if (!installed) {
            setShowExtensionModal(true);
            return;
        }

        if (!taxisCredentials?.username || !taxisCredentials?.password) {
            toast.error("Δεν έχουν οριστεί στοιχεία Taxisnet. Μεταβείτε στις Ρυθμίσεις Λογιστικής.");
            return;
        }

        const resolvedAfm = company?.vatNumber?.trim();
        if (!resolvedAfm) {
            toast.error("Δεν βρέθηκε έγκυρο ΑΦΜ για την τρέχουσα εταιρεία.");
            return;
        }

        const fields = collectF2Payload();
        const isZeroDeclaration = !fields || Object.keys(fields).length === 0;
        if (isZeroDeclaration) {
            toast.info("Μηδενική δήλωση ΦΠΑ: Έναρξη διαδικασίας υποβολής χωρίς ποσά...", { autoClose: 3500 });
        }

        try {
            const formOptions = {
                declarationType: f2FormOptions?.declarationType === '007' ? 1 : 0,
                withReservation: f2FormOptions?.declarationType === '008' ? 1 : 0,
                extraordinaryDeclaration: f2FormOptions?.declarationType === '009' ? 8 : undefined,
                installment523: Number(f2FormOptions?.installment523) === 1 ? 1 : 0,
                returnReason507: f2FormOptions?.returnReason507 ? [f2FormOptions.returnReason507] : [],
                noIbanAccount: Boolean(f2FormOptions?.noIbanAccount),
                noGreekOrSepaAccount: Boolean(f2FormOptions?.noIbanAccount),
                notes: f2FormOptions?.notes || ""
            };

            let extensionPayload = {
                credentials: {
                    username: taxisCredentials.username,
                    password: taxisCredentials.password,
                    isAccountant: Boolean(taxisCredentials.isAccountant),
                    accountantAfm: taxisCredentials.accountantAfm
                },
                year: String(selectedYear),
                companyAfm: resolvedAfm,
                period: {
                    dateFrom: requestData?.date_from,
                    dateTo: requestData?.date_to,
                    type: selectedPeriod.type,
                    index: selectedPeriod.index,
                    month: selectedPeriod.type === 'month' ? selectedPeriod.index : (mTo.isValid() ? mTo.month() + 1 : undefined),
                    quarter: selectedPeriod.type === 'quarter' ? selectedPeriod.index : undefined,
                    year: Number(selectedYear)
                },
                actionType: actionType,
                isZeroDeclaration: isZeroDeclaration
            };

            if (actionType === 'XML_UPLOAD') {
                const xmlString = generateF2XML(
                    fields,
                    formOptions,
                    extensionPayload.period,
                    resolvedAfm
                );
                console.log("Generated F2 XML:\n", xmlString);
                extensionPayload.xmlContent = xmlString;
            } else {
                extensionPayload.fields = fields;
                extensionPayload.options = formOptions;
            }

            activeTaxisCompanyIdRef.current = company?.id;
            activeTaxisAfmRef.current = resolvedAfm;
            setTaxisLoading(true);

            toast.info(actionType === 'XML_UPLOAD'
                ? "Έναρξη δημιουργίας και μεταφόρτωσης αρχείου..."
                : "Έναρξη διαδικασίας. Ανοίγει παράθυρο αυτοματοποίησης...", {autoClose: 4000});

            window.postMessage({type: 'FROM_ERP_AUTOFILL_VAT', payload: extensionPayload}, '*');

        } catch (err) {
            console.log('[ERP Taxis Error]:', err);
            resetTaxisState();
            toast.error("Σφάλμα κατά την προετοιμασία των στοιχείων δήλωσης.");
        }
    };

    const handleCreateAccountingRecord = () => {
        setLoading(true);
        const monthStr = moment(requestData.date_from, "DD/MM/YYYY").format("MM");

        axios.post(`${process.env.REACT_APP_API_URL2}/accounting/generate-accounting-record-from-rules`, {
            company: company.id,
            month: monthStr,
            year: company.year,
            date_from: requestData.date_from,
            date_to: requestData.date_to
        }, {
            headers: {"Content-Type": "application/json"}
        }).then((res) => {
            setLoading(false);
            if (res.data.status === "200" && res.data.data) {
                const fullRecord = res.data.data;

                if (!fullRecord.category || !fullRecord.subCategory) {
                    toast.warning("Δεν έχουν οριστεί Λογιστική Κατηγορία και Υποκατηγορία στις Ρυθμίσεις.");
                    return;
                }

                if (fullRecord.accounts.length === 0) {
                    toast.info("Δεν προέκυψε καμία γραμμή εγγραφής (όλα τα υπόλοιπα ήταν μηδενικά).");
                } else {
                    toast.success(`Υπολογίστηκαν επιτυχώς ${fullRecord.accounts.length} γραμμές!`);
                }

                const currentRecordBase = {...newAccountingRecord};

                dispatch(setNewAccountingRecord({
                    ...currentRecordBase,
                    ...fullRecord,
                    installation: "0",
                    date: moment().toDate(),
                    time: moment().format("HH:mm")
                }));

                dispatch(openNewAccountingRecord(TABS_DATA));

            } else {
                toast.warning(res.data.message || "Δεν βρέθηκαν κανόνες δημιουργίας.");
            }
        }).catch((err) => {
            setLoading(false);
            toast.error("Σφάλμα κατά τον υπολογισμό της εγγραφής.");
            console.log(err);
        });
    };

    const vatCodesToCheck = [
        301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315,
        331, 332, 333, 334, 335, 336, 337, 338, 339,
        361, 362, 363, 364, 365, 366, 367,
        381, 382, 383, 384, 385, 386, 387,
        342, 345, 348, 349,
        401, 402, 403, 404, 410, 411, 422, 423, 428, 430, 470, 480, 483,
        502, 503, 505, 511, 513, 514,
        906, 907, 908, 912
    ];

    const erpDiscrepancies = [];
    const accountingDiscrepancies = [];

    if (responseData) {
        vatCodesToCheck.forEach((code) => {
            const data = getCellData(code);
            if (Math.abs(data.erp - data.myData) > 0.1) {
                erpDiscrepancies.push(code);
            }
            if (Math.abs(data.accounting - data.myData) > 0.1) {
                accountingDiscrepancies.push(code);
            }
        });
    }

    return (
        <React.Fragment>
            <style>{`
                .f2-layout table td.p-0 {
                    height: 1px;
                }
                .f2-no-spinners::-webkit-inner-spin-button,
                .f2-no-spinners::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
                .f2-no-spinners {
                    -moz-appearance: textfield;
                }
                .f2-no-spinners::placeholder {
                    color: #b0c4de;
                    font-weight: normal;
                }
                .f2-declaration-checkbox {
                    -webkit-appearance: none;
                    appearance: none;
                    margin: 0;
                    width: 14px;
                    height: 14px;
                    border: 1px solid #777;
                    border-radius: 2px;
                    background-color: #fff;
                    cursor: pointer;
                    display: inline-grid;
                    place-content: center;
                    outline: none;
                }

                .f2-declaration-checkbox:checked {
                    background-color: #76AEB7;
                    border-color: #76AEB7;
                }

                .f2-declaration-checkbox:checked::before {
                    content: "";
                    width: 4px;
                    height: 8px;
                    border: solid #ffffff;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                    margin-bottom: 2px;
                }
                .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
                    background-color: #76AEB7;
                    border-color: #76AEB7;
                }
                .custom-checkbox .custom-control-input:focus ~ .custom-control-label::before {
                    box-shadow: 0 0 0 0.2rem #ACCED3;
                }
            `}</style>
            <Card className="mb-3 border-0 shadow-sm print-hide w-100" style={{position: "relative", zIndex: 100}}>
                <Card.Body className="p-3">
                    <Row className="align-items-center">
                        <Col md={2}>
                            <DateHelper dateFromAndToFunction={(from, to) => dispatch(setF2ReportRequestData({
                                ...requestData,
                                date_from: from,
                                date_to: to
                            }))}/>
                        </Col>
                        <Col md={2}>
                            <DateBox
                                name={"date_from"}
                                selected={mFrom.isValid() ? mFrom.toDate() : null}
                                label={t('SalesMyData.dateFrom')}
                                onChange={(e) => dispatch(setF2ReportRequestData({
                                    ...requestData,
                                    date_from: moment(e).format("DD/MM/YYYY")
                                }))}
                                allowOtherYears={false}
                            />
                        </Col>
                        <Col md={2}>
                            <DateBox
                                name={"date_to"}
                                selected={mTo.isValid() ? mTo.toDate() : null}
                                label={t('SalesMyData.dateTo')}
                                onChange={(e) => dispatch(setF2ReportRequestData({
                                    ...requestData,
                                    date_to: moment(e).format("DD/MM/YYYY")
                                }))}
                                allowOtherYears={false}
                            />
                        </Col>
                        <Col md={6}>
                            <Button onClick={fetchF2Data} disabled={loading} className="mt-4" variant="primary">
                                Υπολογισμός Φ2 {loading && <Spinner className="ml-2" animation="border" size="sm"/>}
                            </Button>
                            <Button onClick={handleCreateAccountingRecord} className="mt-4 ml-2" variant="primary">
                                Δημιουργία Λογιστικής Εγγραφής
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {responseData && (
                <>
                    <div className="w-100 mb-1 print-hide" style={{maxWidth: "1200px", margin: "0 auto"}}>
                        <Row>
                            <Col md={6} className="mb-2">
                                {erpDiscrepancies.length > 0 ? (
                                    <Card style={{backgroundColor: "#f8d7da", borderColor: "#f5c6cb"}}
                                          className="shadow-sm">
                                        <Card.Body className="py-2 px-3 d-flex align-items-center">
                                            <i className="fa-solid fa-triangle-exclamation fa-lg mr-3"
                                               style={{color: "#721c24"}}></i>
                                            <div>
                                                <div className="font-weight-bold mb-1"
                                                     style={{fontSize: "0.85rem", color: "#721c24"}}>
                                                    Ασυμφωνία Εμπορικού (ERP) με myDATA
                                                </div>
                                                <div style={{fontSize: "0.75rem", color: "#842029"}}>
                                                    Εντοπίστηκαν αποκλίσεις
                                                    σε <strong>{erpDiscrepancies.length} κωδικούς:</strong> {erpDiscrepancies.map(c => `${c}`).join(', ')}.
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ) : (
                                    <Card style={{backgroundColor: "#d1e7dd", borderColor: "#badbcc"}}
                                          className="shadow-sm">
                                        <Card.Body className="py-2 px-3 d-flex align-items-center">
                                            <i className="fa-solid fa-circle-check fa-lg mr-3"
                                               style={{color: "#0f5132"}}></i>
                                            <div>
                                                <div className="font-weight-bold mb-1"
                                                     style={{fontSize: "0.85rem", color: "#0f5132"}}>
                                                    Απόλυτη Συμφωνία Εμπορικού (ERP) με myDATA
                                                </div>
                                                <div style={{fontSize: "0.75rem", color: "#146c43"}}>
                                                    Όλοι οι κωδικοί του Εμπορικού ταυτίζονται πλήρως με τα δεδομένα του
                                                    myDATA.
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Col>
                            <Col md={6} className="mb-2">
                                {accountingDiscrepancies.length > 0 ? (
                                    <Card style={{backgroundColor: "#f8d7da", borderColor: "#f5c6cb"}}
                                          className="shadow-sm">
                                        <Card.Body className="py-2 px-3 d-flex align-items-center">
                                            <i className="fa-solid fa-triangle-exclamation fa-lg mr-3"
                                               style={{color: "#721c24"}}></i>
                                            <div>
                                                <div className="font-weight-bold mb-1"
                                                     style={{fontSize: "0.85rem", color: "#721c24"}}>
                                                    Ασυμφωνία Λογιστικής με myDATA
                                                </div>
                                                <div style={{fontSize: "0.75rem", color: "#842029"}}>
                                                    Εντοπίστηκαν αποκλίσεις
                                                    σε <strong>{accountingDiscrepancies.length} κωδικούς:</strong> {accountingDiscrepancies.map(c => `${c}`).join(', ')}.
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ) : (
                                    <Card style={{backgroundColor: "#d1e7dd", borderColor: "#badbcc"}}
                                          className="shadow-sm">
                                        <Card.Body className="py-2 px-3 d-flex align-items-center">
                                            <i className="fa-solid fa-circle-check fa-lg mr-3"
                                               style={{color: "#0f5132"}}></i>
                                            <div>
                                                <div className="font-weight-bold mb-1"
                                                     style={{fontSize: "0.85rem", color: "#0f5132"}}>
                                                    Απόλυτη Συμφωνία Λογιστικής με myDATA
                                                </div>
                                                <div style={{fontSize: "0.75rem", color: "#146c43"}}>
                                                    Όλοι οι κωδικοί της Γενικής Λογιστικής ταυτίζονται πλήρως με τα
                                                    δεδομένα του myDATA.
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Col>
                        </Row>
                    </div>
                    <div
                        className="w-100 pb-2"
                        style={{
                            overflowX: "auto",
                            overflowY: "hidden",
                            justifyContent: zoomLevel > 1 ? "flex-start" : "center",
                            cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "auto"
                        }}
                        onMouseDown={(e) => {
                            if (zoomLevel <= 1) return;
                            if (['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT'].includes(e.target.tagName)) {
                                return;
                            }
                            setIsDragging(true);
                            setDragStartX(e.clientX);
                            setDragScrollLeft(e.currentTarget.scrollLeft);
                        }}
                        onMouseLeave={() => setIsDragging(false)}
                        onMouseUp={() => setIsDragging(false)}
                        onMouseMove={(e) => {
                            if (!isDragging) return;
                            e.preventDefault();
                            const walk = (e.clientX - dragStartX) * 1.5;
                            e.currentTarget.scrollLeft = dragScrollLeft - walk;
                        }}
                    >
                        <div style={{width: "1200px", margin: "0 auto", userSelect: isDragging ? "none" : "auto"}}>
                            <div className="bg-white f2-layout"
                                 style={{
                                     ...formStyle,
                                     width: "1200px",
                                     zoom: zoomLevel,
                                     userSelect: isDragging ? "none" : "auto"
                                 }}>
                                <F2ColumnsContext.Provider value={f2VisibleColumns}>
                                    <div className="position-relative w-100 bg-white"
                                         style={{
                                             borderBottom: borderDark,
                                             padding: "10px",
                                             paddingBottom: "15px",
                                             minHeight: "260px"
                                         }}>
                                        <div className="position-absolute" style={{
                                            top: 0, left: 0, width: "220px", height: "220px",
                                            backgroundColor: "#009FE3",
                                            clipPath: "polygon(0 0, 100% 0, 0 100%)",
                                            zIndex: 1,
                                            pointerEvents: "none"
                                        }}>
                                            <div style={{
                                                position: "absolute",
                                                top: "15px",
                                                left: "25px",
                                                color: "#fff",
                                                fontSize: "3.5rem",
                                                fontWeight: "bold",
                                                lineHeight: 1,
                                                fontFamily: "Arial, sans-serif"
                                            }}>
                                                Φ<span style={{
                                                fontSize: "2.2rem",
                                                position: "relative",
                                                top: "4px",
                                                marginLeft: "2px"
                                            }}>2</span>
                                            </div>
                                            <div style={{
                                                position: "absolute",
                                                top: "95px",
                                                left: "95px",
                                                width: "300px",
                                                transform: "translate(-50%, -50%) rotate(-45deg)",
                                                textAlign: "center"
                                            }}>
                                                <div style={{
                                                    width: "100%",
                                                    height: "1px",
                                                    backgroundColor: "#fff",
                                                    opacity: 0.9,
                                                    marginBottom: "4px"
                                                }}></div>
                                                <div style={{
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    fontSize: "0.75rem",
                                                    letterSpacing: "16px",
                                                    paddingLeft: "16px"
                                                }}>
                                                    TAXIS
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-between">
                                            <div className="d-flex flex-column"
                                                 style={{
                                                     width: "55%",
                                                     paddingTop: "5px",
                                                     paddingLeft: "160px",
                                                     zIndex: 2
                                                 }}>
                                                <div className="text-center mb-4">
                                                    <h4 className="font-weight-bold mb-0"
                                                        style={{fontSize: "1.6rem", color: "#000"}}>ΔΗΛΩΣΗ Φ.Π.Α.</h4>
                                                    <div style={{
                                                        fontSize: "0.75rem",
                                                        color: "#005a9c",
                                                        fontWeight: "bold",
                                                        letterSpacing: "-0.5px"
                                                    }}>Υποβάλλεται σε δύο (2) αντίτυπα (έντυπη υποβολή).
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center position-relative mb-1"
                                                     style={{width: "95%"}}>
                                                    <div className="d-flex w-100" style={{border: borderDark}}>
                                                        <div
                                                            className="d-flex align-items-end p-1 border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "65%",
                                                                fontSize: "0.7rem"
                                                            }}>
                                                            <span
                                                                className="font-weight-bold mr-1">ΑΡΜΟΔΙΑ Δ.Ο.Υ.</span>
                                                            <span className="flex-fill" style={{
                                                                borderBottom: "1px dotted #000",
                                                                height: "12px",
                                                                overflow: "hidden",
                                                                whiteSpace: "nowrap"
                                                            }}>{doyLabel}</span>
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{borderColor: "#000", width: "10%", ...grayBg}}>
                                                            003
                                                        </div>
                                                        <div className="position-relative" style={{width: "25%"}}>
                                                            <div
                                                                className="d-flex w-100 h-100 align-items-center justify-content-around font-weight-bold pb-1"
                                                                style={{fontSize: "0.75rem"}}>
                                                                {doyCode ? doyCode.split('').map((char, index) => <span
                                                                    key={index}>{char}</span>) : ""}
                                                            </div>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(4)].map((_, i) => (
                                                                    <div key={i} className="flex-fill"
                                                                         style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span className="position-absolute font-weight-bold"
                                                          style={{right: "-12px", top: "2px"}}>*</span>
                                                </div>
                                                <div className="d-flex flex-column mb-3"
                                                     style={{width: "85%", border: borderDark, marginLeft: "10%"}}>
                                                    <div className="d-flex" style={{borderBottom: borderDark}}>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{borderColor: "#000", width: "12%", ...grayBg}}>
                                                            005
                                                        </div>
                                                        <div className="flex-fill text-center p-1"
                                                             style={{fontSize: "0.7rem", color: "#005a9c"}}>
                                                            ΗΜΕΡΟΛΟΓΙΑΚΗ ΠΕΡΙΟΔΟΣ
                                                        </div>
                                                    </div>
                                                    <div className="d-flex" style={{height: "22px"}}>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "12%",
                                                                fontSize: "0.7rem",
                                                                color: "#005a9c"
                                                            }}>από
                                                        </div>
                                                        <div className="position-relative border-right border-dark"
                                                             style={{borderColor: "#000", width: "38%"}}>
                                                            <div
                                                                className="d-flex align-items-center justify-content-center w-100 h-100 font-weight-bold"
                                                                style={{
                                                                    letterSpacing: "2px",
                                                                    fontSize: "0.75rem"
                                                                }}>{requestData?.date_from}</div>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(4)].map((_, i) => (
                                                                    <div key={i} className="flex-fill"
                                                                         style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "12%",
                                                                fontSize: "0.7rem",
                                                                color: "#005a9c"
                                                            }}>έως
                                                        </div>
                                                        <div className="position-relative" style={{width: "38%"}}>
                                                            <div
                                                                className="d-flex align-items-center justify-content-center w-100 h-100 font-weight-bold"
                                                                style={{
                                                                    letterSpacing: "2px",
                                                                    fontSize: "0.75rem"
                                                                }}>{requestData?.date_to}</div>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(4)].map((_, i) => (
                                                                    <div key={i} className="flex-fill"
                                                                         style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-column pt-1"
                                                     style={{width: "90%", border: borderDark, marginLeft: "5%"}}>
                                                    <div className="text-center p-1"
                                                         style={{
                                                             borderBottom: borderDark,
                                                             color: "#005a9c",
                                                             fontSize: "0.8rem",
                                                             letterSpacing: "1px"
                                                         }}>
                                                        ΕΙΔΟΣ &nbsp;&nbsp;ΔΗΛΩΣΗΣ
                                                    </div>
                                                    <div className="d-flex w-100" style={{height: "25px"}}>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{borderColor: "#000", width: "11%", ...grayBg}}>007
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark text-center"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "15%",
                                                                fontSize: "0.55rem",
                                                                lineHeight: 1,
                                                                color: "#005a9c"
                                                            }}>
                                                            ΤΡΟΠΟ-<br/>ΠΟΙΗΤΙΚΗ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark"
                                                            style={{borderColor: "#000", width: "7.33%"}}>
                                                            <input type="checkbox" className="f2-declaration-checkbox"
                                                                   style={{cursor: "pointer"}}
                                                                   checked={f2FormOptions.declarationType === "007"}
                                                                   onChange={() => handleDeclarationTypeChange("007")}
                                                            />
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{borderColor: "#000", width: "11%", ...grayBg}}>008
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark text-center"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "15%",
                                                                fontSize: "0.55rem",
                                                                lineHeight: 1,
                                                                color: "#005a9c"
                                                            }}>
                                                            ΜΕ ΕΠΙ-<br/>ΦΥΛΑΞΗ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark"
                                                            style={{borderColor: "#000", width: "7.33%"}}>
                                                            <input type="checkbox" className="f2-declaration-checkbox"
                                                                   style={{cursor: "pointer"}}
                                                                   checked={f2FormOptions.declarationType === "008"}
                                                                   onChange={() => handleDeclarationTypeChange("008")}
                                                            />
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{borderColor: "#000", width: "11%", ...grayBg}}>009
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-right border-dark text-center"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "15%",
                                                                fontSize: "0.55rem",
                                                                lineHeight: 1,
                                                                color: "#005a9c"
                                                            }}>
                                                            ΕΚΤΑΚΤΗ<br/>ΔΗΛΩΣΗ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "7.33%",
                                                                borderRight: "none"
                                                            }}>
                                                            <input type="checkbox" className="f2-declaration-checkbox"
                                                                   style={{cursor: "pointer"}}
                                                                   checked={f2FormOptions.declarationType === "009"}
                                                                   onChange={() => handleDeclarationTypeChange("009")}
                                                            />
                                                        </div>
                                                        <div className="flex-fill"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex flex-column align-items-end"
                                                 style={{width: "45%", paddingTop: "10px", zIndex: 2}}>
                                                <div className="position-relative mb-4"
                                                     style={{width: "92%", border: borderDark}}>
                                                    <div className="d-flex w-100"
                                                         style={{borderBottom: borderDark, height: "26px"}}>
                                                        <div
                                                            className="d-flex align-items-center font-weight-bold border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "28%",
                                                                fontSize: "0.65rem",
                                                                lineHeight: "1.1",
                                                                paddingLeft: "4px",
                                                                color: "#005a9c"
                                                            }}>
                                                            ΑΡΙΘΜΟΣ<br/>ΔΗΛΩΣΗΣ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "15%", ...grayBg,
                                                                fontSize: "0.7rem"
                                                            }}>
                                                            001
                                                        </div>
                                                        <div
                                                            className="d-flex flex-column position-relative border-right border-dark"
                                                            style={{borderColor: "#000", width: "42%"}}>
                                                            <div className="text-center w-100" style={{
                                                                fontSize: "0.6rem",
                                                                marginTop: "1px",
                                                                color: "#005a9c"
                                                            }}>ΑΡΙΘΜΟΣ
                                                            </div>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(6)].map((_, i) => (
                                                                    <div key={i} className="flex-fill"
                                                                         style={{borderRight: i === 5 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="d-flex flex-column position-relative"
                                                             style={{width: "15%"}}>
                                                            <div className="text-center w-100"
                                                                 style={{
                                                                     fontSize: "0.6rem",
                                                                     marginTop: "1px",
                                                                     color: "#005a9c"
                                                                 }}>ΕΤΟΣ
                                                            </div>
                                                            <div
                                                                className="d-flex w-100 h-100 align-items-center justify-content-center font-weight-bold pb-1"
                                                                style={{fontSize: "0.75rem", letterSpacing: "1px"}}>
                                                                {selectedYear}
                                                            </div>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(2)].map((_, i) => (
                                                                    <div key={i} className="flex-fill"
                                                                         style={{borderRight: i === 1 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex w-100" style={{height: "26px"}}>
                                                        <div
                                                            className="d-flex align-items-center font-weight-bold border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "28%",
                                                                fontSize: "0.65rem",
                                                                lineHeight: "1.1",
                                                                paddingLeft: "4px",
                                                                letterSpacing: "-0.3px",
                                                                color: "#005a9c"
                                                            }}>
                                                            ΗΜΕΡΟΜΗΝΙΑ<br/>ΥΠΟΒΟΛΗΣ ΔΗΛΩΣΗΣ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                            style={{
                                                                borderColor: "#000",
                                                                width: "15%", ...grayBg,
                                                                fontSize: "0.7rem"
                                                            }}>
                                                            002
                                                        </div>
                                                        <div
                                                            className="d-flex border-right border-dark position-relative"
                                                            style={{borderColor: "#000", width: "42%"}}>
                                                            <div
                                                                className="d-flex w-100 h-100 align-items-center justify-content-center font-weight-bold pb-1"
                                                                style={{fontSize: "0.75rem", letterSpacing: "1px"}}>
                                                                {moment().format("DD/MM/YYYY")}
                                                            </div>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(10)].map((_, i) => (
                                                                    <div key={i} className="flex-fill"
                                                                         style={{borderRight: i === 9 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="position-relative" style={{width: "15%"}}>
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                <div className="flex-fill"
                                                                     style={{borderRight: borderDark}}></div>
                                                                <div className="flex-fill"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="position-absolute font-weight-bold"
                                                         style={{right: "-12px", top: "5px", fontSize: "0.75rem"}}>*
                                                    </div>
                                                    <div className="position-absolute font-weight-bold"
                                                         style={{right: "-12px", bottom: "5px", fontSize: "0.75rem"}}>*
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-column justify-content-end"
                                                     style={{width: "100%"}}>
                                                    <div className="d-flex justify-content-end" style={{
                                                        paddingRight: "0px",
                                                        marginBottom: "-1px",
                                                        zIndex: 1,
                                                        position: "relative"
                                                    }}>
                                                        <div className="d-flex" style={{
                                                            border: borderDark,
                                                            backgroundColor: "#fff",
                                                            height: "22px"
                                                        }}>
                                                            <div
                                                                className="d-flex align-items-center px-2 font-weight-bold border-right border-dark"
                                                                style={{borderColor: "#000", fontSize: "0.65rem"}}>ΕΤΟΣ
                                                            </div>
                                                            <div
                                                                className="d-flex align-items-center justify-content-center font-weight-bold border-right border-dark"
                                                                style={{
                                                                    borderColor: "#000", ...grayBg,
                                                                    width: "35px",
                                                                    fontSize: "0.7rem"
                                                                }}>004
                                                            </div>
                                                            <div
                                                                className="position-relative d-flex align-items-center justify-content-center font-weight-bold"
                                                                style={{width: "55px", fontSize: "0.65rem"}}>
                                                                {selectedYear}
                                                                <div className="d-flex w-100 position-absolute"
                                                                     style={{bottom: 0, height: "4px"}}>
                                                                    {[...Array(4)].map((_, i) => (
                                                                        <div key={i} className="flex-fill"
                                                                             style={{borderRight: i === 3 ? "none" : borderDark}}></div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-white"
                                                         style={{border: borderDark, position: "relative", zIndex: 0}}>
                                                        <div className="text-center font-weight-bold py-1"
                                                             style={{
                                                                 borderBottom: borderDark,
                                                                 color: "#005a9c",
                                                                 fontSize: "0.75rem"
                                                             }}>
                                                            ΦΟΡΟΛΟΓΙΚΗ ΠΕΡΙΟΔΟΣ (Διαγραμμίστε με X)
                                                        </div>
                                                        <table className="w-100 text-center m-0"
                                                               style={{
                                                                   tableLayout: "fixed",
                                                                   borderCollapse: "collapse"
                                                               }}>
                                                            <colgroup>
                                                                <col style={{width: "22%"}}/>
                                                                <col style={{width: "8%"}}/>
                                                                {[...Array(12)].map((_, i) => (
                                                                    <col key={`col-${i}`} style={{width: "5.83%"}}/>
                                                                ))}
                                                            </colgroup>
                                                            <tbody>
                                                            <tr>
                                                                <td className="align-middle p-0" style={{
                                                                    fontSize: "0.65rem",
                                                                    borderBottom: borderDark,
                                                                    borderRight: borderDark
                                                                }}>ΜΗΝΑΣ
                                                                </td>
                                                                <td rowSpan={4} className="align-middle p-0" style={{
                                                                    ...grayBg,
                                                                    borderRight: borderDark,
                                                                    borderLeft: borderDark
                                                                }}>
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center font-weight-bold w-100 h-100"
                                                                        style={{fontSize: "0.7rem"}}>006
                                                                    </div>
                                                                </td>
                                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                                                                    <td key={`m-${m}`} className="p-0 align-middle"
                                                                        style={{
                                                                            height: "22px",
                                                                            borderBottom: borderDark,
                                                                            borderRight: m === 12 ? "none" : borderDark
                                                                        }}>
                                                                        <CrossableNumber number={m}
                                                                                         isCrossed={selectedPeriod.type === 'month' && selectedPeriod.index === m}/>
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                            <tr>
                                                                <td className="align-middle p-0" style={{
                                                                    fontSize: "0.65rem",
                                                                    borderBottom: borderDark,
                                                                    borderRight: borderDark
                                                                }}>ΤΡΙΜΗΝΟ
                                                                </td>
                                                                {[1, 2, 3, 4].map(m => (
                                                                    <td key={`q-${m}`} className="p-0 align-middle"
                                                                        style={{
                                                                            height: "22px",
                                                                            borderBottom: borderDark,
                                                                            borderRight: m === 4 ? borderDark : borderDark
                                                                        }}>
                                                                        <CrossableNumber number={m}
                                                                                         isCrossed={selectedPeriod.type === 'quarter' && selectedPeriod.index === m}/>
                                                                    </td>
                                                                ))}
                                                                <td colSpan={8} style={{border: "none"}}></td>
                                                            </tr>
                                                            <tr>
                                                                <td className="align-middle p-0" style={{
                                                                    fontSize: "0.65rem",
                                                                    borderBottom: borderDark,
                                                                    borderRight: borderDark
                                                                }}>ΕΞΑΜΗΝΟ
                                                                </td>
                                                                {[1, 2].map(m => (
                                                                    <td key={`s-${m}`} className="p-0 align-middle"
                                                                        style={{
                                                                            height: "22px",
                                                                            borderBottom: borderDark,
                                                                            borderRight: m === 2 ? borderDark : borderDark
                                                                        }}>
                                                                        <CrossableNumber number={m}
                                                                                         isCrossed={selectedPeriod.type === 'semester' && selectedPeriod.index === m}/>
                                                                    </td>
                                                                ))}
                                                                <td colSpan={10} style={{border: "none"}}></td>
                                                            </tr>
                                                            <tr>
                                                                <td className="align-middle p-0" style={{
                                                                    fontSize: "0.65rem",
                                                                    borderBottom: "none",
                                                                    borderRight: borderDark
                                                                }}>ΕΤΗΣΙΑ
                                                                </td>
                                                                <td className="p-0 align-middle" style={{
                                                                    height: "22px",
                                                                    borderBottom: "none",
                                                                    borderRight: borderDark
                                                                }}>
                                                                    <CrossableNumber number={1}
                                                                                     isCrossed={selectedPeriod.type === 'year' && selectedPeriod.index === 1}/>
                                                                </td>
                                                                <td colSpan={11} style={{border: "none"}}></td>
                                                            </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white" style={{border: borderDark}}>
                                        <div className="p-1 d-flex align-items-center"
                                             style={{borderBottom: borderDark, fontSize: "0.75rem", color: "#000"}}>
                                            <span className="font-weight-bold">Α. ΠΙΝΑΚΑΣ ΜΕ ΤΑ ΣΤΟΙΧΕΙΑ ΤΟΥ ΥΠΟΚΕΙΜΕΝΟΥ ΣΤΟ ΦΟΡΟ Ή ΛΗΠΤΗ</span>
                                        </div>
                                        <div className="d-flex">
                                            <div style={{width: "2%", borderRight: borderDark}}></div>
                                            <div className="d-flex flex-column flex-fill">
                                                <div className="d-flex w-100"
                                                     style={{borderBottom: borderDark, height: "30px"}}>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                                        style={{
                                                            width: "5%", ...grayBg,
                                                            borderRight: borderDark,
                                                            fontSize: "0.7rem"
                                                        }}>
                                                        101
                                                    </div>
                                                    <div className="flex-fill d-flex flex-column px-2"
                                                         style={{position: "relative"}}>
                                                    <span style={{
                                                        fontSize: "0.65rem",
                                                        color: "#005a9c",
                                                        marginTop: "1px"
                                                    }}>ΕΠΩΝΥΜΟ Ή ΕΠΩΝΥΜΙΑ</span>
                                                        <span className="font-weight-bold" style={{
                                                            fontSize: "0.75rem",
                                                            marginTop: "-3px"
                                                        }}>{company?.name || ""}</span>
                                                    </div>
                                                </div>
                                                <div className="d-flex w-100" style={{height: "30px"}}>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                                        style={{
                                                            width: "5%", ...grayBg,
                                                            borderRight: borderDark,
                                                            fontSize: "0.7rem"
                                                        }}>
                                                        102
                                                    </div>
                                                    <div className="d-flex flex-column px-2"
                                                         style={{width: "42%", borderRight: borderDark}}>
                                    <span style={{
                                        fontSize: "0.65rem",
                                        color: "#005a9c",
                                        marginTop: "1px"
                                    }}>ΟΝΟΜΑ</span>
                                                    </div>

                                                    <div
                                                        className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                                        style={{
                                                            width: "5%", ...grayBg,
                                                            borderRight: borderDark,
                                                            fontSize: "0.7rem"
                                                        }}>
                                                        103
                                                    </div>
                                                    <div className="d-flex flex-column px-2"
                                                         style={{width: "23%", borderRight: borderDark}}>
                                                    <span style={{
                                                        fontSize: "0.65rem",
                                                        color: "#005a9c",
                                                        marginTop: "1px"
                                                    }}>ΟΝΟΜΑ ΠΑΤΕΡΑ</span>
                                                    </div>

                                                    <div
                                                        className="d-flex align-items-center justify-content-center font-weight-bold text-dark"
                                                        style={{
                                                            width: "5%", ...grayBg,
                                                            borderRight: borderDark,
                                                            fontSize: "0.7rem"
                                                        }}>
                                                        104
                                                    </div>
                                                    <div
                                                        className="flex-fill d-flex align-items-center position-relative px-2">
                                    <span className="align-self-start" style={{
                                        fontSize: "0.65rem",
                                        color: "#005a9c",
                                        marginTop: "1px",
                                        marginRight: "10px"
                                    }}>Α.Φ.Μ.</span>
                                                        <div className="flex-fill position-relative h-100">
                                                            <div className="d-flex w-100 position-absolute"
                                                                 style={{bottom: 0, height: "4px"}}>
                                                                {[...Array(9)].map((_, i) => (
                                                                    <div key={`afm-tick-${i}`} className="flex-fill"
                                                                         style={{borderRight: i === 8 ? "none" : borderDark}}></div>
                                                                ))}
                                                            </div>
                                                            <div
                                                                className="d-flex justify-content-around w-100 position-absolute"
                                                                style={{
                                                                    bottom: "2px",
                                                                    fontWeight: "bold",
                                                                    fontSize: "0.7rem",
                                                                    letterSpacing: "2px"
                                                                }}>
                                                                {company?.vatNumber ? company.vatNumber.split('').map((digit, index) => (
                                                                    <span key={`digit-${index}`}>{digit}</span>
                                                                )) : ""}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white" style={{border: borderDark}}>
                                        <div className="p-1 d-flex align-items-center"
                                             style={{borderBottom: borderDark, fontSize: "0.75rem", color: "#000"}}>
                                            <span className="font-weight-bold mr-2">Β. ΠΙΝΑΚΑΣ ΕΚΡΟΩΝ - ΕΙΣΡΟΩΝ</span>
                                            <span className="font-weight-normal">μετά την αφαίρεση (κατά συντελεστή) των επιστροφών - εκπτώσεων.</span>
                                        </div>

                                        <Row className="no-gutters">
                                            <Col lg={6} className="border-right border-dark"
                                                 style={{borderColor: "#000", borderRightWidth: "2px"}}>
                                                <table className="w-100 text-center m-0"
                                                       style={{tableLayout: "fixed", borderCollapse: "collapse"}}>
                                                    <colgroup>
                                                        <col style={{width: "4%"}}/>
                                                        <col style={{width: "24%"}}/>
                                                        <col style={{width: "5.5%"}}/>
                                                        <col style={{width: "27.5%"}}/>
                                                        <col style={{width: "5%"}}/>
                                                        <col style={{width: "5.5%"}}/>
                                                        <col style={{width: "28.5%"}}/>
                                                    </colgroup>
                                                    <thead>
                                                    <tr className="align-middle">
                                                        <th rowSpan={2} className="p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}>α
                                                        </th>
                                                        <th colSpan={3} className="p-1" style={{
                                                            fontSize: "0.65rem",
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "none"
                                                        }}>
                                                            ΕΚΡΟΕΣ φορολογητέες (πωλήσεις αγαθών,παροχή<br/>υπηρεσιών
                                                            κλπ),
                                                            ΕΝΔΟΚ.
                                                            ΑΠΟΚΤ. & ΠΡΑΞ. ΛΗΠΤΗ
                                                        </th>
                                                        <th rowSpan={2} className="p-1" style={{
                                                            fontSize: "0.65rem",
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}>Συντ.<br/>ΦΠΑ<br/>%
                                                        </th>
                                                        <th colSpan={2} className="p-1"
                                                            style={{fontSize: "0.6rem", borderBottom: "none"}}>ΦΟΡΟΣ
                                                            ΕΚΡΟΩΝ<br/>που
                                                            αναλογεί
                                                        </th>
                                                    </tr>
                                                    <tr style={{borderBottom: "1px solid #000"}}>
                                                        <th colSpan={2} style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}></th>
                                                        <th className="p-0 align-bottom"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleHeader/>
                                                        </th>
                                                        <th style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}></th>
                                                        <th className="p-0 align-bottom"
                                                            style={{borderBottom: "1px solid #000"}}>
                                                            <TripleHeader/>
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    <tr>
                                                        <td rowSpan={8} style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}></td>
                                                        <td rowSpan={4} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>
                                                            I. ΕΚΡΟΕΣ,<br/>ΕΝΔΟΚ. ΑΠΟΚΤΗΣΕΙΣ<br/>& ΠΡΑΞΕΙΣ ΛΗΠΤΗ<br/>σε
                                                            λοιπή Ελλάδα
                                                            εκτός<br/>από τα νησιά Αιγαίου.
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>301
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(301)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>13
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>331
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(331)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>302
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(302)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>6
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>332
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(332)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>303
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(303)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>24
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>333
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(333)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>308
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(308)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>4
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>338
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(338)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td rowSpan={4} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>
                                                            II. ΕΚΡΟΕΣ,<br/>ΕΝΔΟΚ. ΑΠΟΚΤΗΣΕΙΣ<br/>& ΠΡΑΞΕΙΣ ΛΗΠΤΗ<br/>στα
                                                            νησιά
                                                            Αιγαίου<br/>και από λοιπή Ελλάδα<br/>προς τα νησιά αυτά
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>304
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(304)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>9
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>334
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(334)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>305
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(305)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>4
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>335
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(335)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>306
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(306)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>17
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>336
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(336)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>309
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(309)} /></td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>3
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>339
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(339)} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2}
                                                            className="align-middle p-1 text-center font-weight-bold"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000",
                                                                fontSize: "0.65rem"
                                                            }}>
                                                            ΣΥΝΟΛΟ<br/>ΦΟΡΟΛ/ΤΕΩΝ ΕΚΡΟΩΝ
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>307
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(307)} /></td>
                                                        <td className="align-middle p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.65rem"
                                                        }}>ΣΥΝ.<br/>ΦΟΡ.
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>337
                                                        </td>
                                                        <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                            <TripleCell {...getCellData(337)} /></td>
                                                    </tr>
                                                    {[
                                                        {code: 342, label: "Ενδοκοινοτικές παραδόσεις"},
                                                        {code: 345, label: "Ενδοκ/τικές παροχές υπηρεσιών άρθρ.14.2.α"},
                                                        {code: 348, label: "Εξαγωγές & απαλλαγές πλοίων & αεροσκαφών"},
                                                        {
                                                            code: 349,
                                                            label: "Λοιπές εκροές χωρίς ΦΠΑ με δικαίωμα έκπτωσης"
                                                        },
                                                        {
                                                            code: 310,
                                                            label: "Εκροές απαλλ/νες & εξαιρ/νες χωρίς δικαίωμα έκπτωσης"
                                                        },
                                                        {code: 311, label: "ΣΥΝΟΛΟ ΕΚΡΟΩΝ"},
                                                        {
                                                            code: 314,
                                                            label: "Ενδοκοινοτικές Αποκτήσεις χωρίς δικαίωμα έκπτωσης που δεν συμμετέχουν στον ΚΥΚΛΟ ΕΡΓΑΣΙΩΝ ΦΠΑ"
                                                        },
                                                        {
                                                            code: 315,
                                                            label: "Ενδοκοινοτικές Λήψεις υπηρεσιών χωρίς δικαίωμα έκπτ. που δεν συμμετέχουν στον ΚΥΚΛΟ ΕΡΓΑΣΙΩΝ ΦΠΑ"
                                                        },
                                                        {
                                                            code: 313,
                                                            label: "Λοιπές πράξεις που δεν συμμετέχουν στον ΚΥΚΛΟ ΕΡΓΑΣΙΩΝ ΦΠΑ"
                                                        },
                                                        {code: 312, label: "Κύκλος εργασιών ΦΠΑ"}
                                                    ].map((item, index) => (
                                                        <tr key={item.code}>
                                                            <td colSpan={2} className="align-middle text-center p-0"
                                                                style={{
                                                                    borderRight: "1px solid #000",
                                                                    borderBottom: index === 9 ? "none" : "1px solid #000",
                                                                    fontSize: "0.6rem"
                                                                }}>
                                                                {item.label}
                                                            </td>
                                                            <td className="align-middle font-weight-bold p-0" style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: index === 9 ? "none" : "1px solid #000", ...grayBg,
                                                                fontSize: "0.6rem"
                                                            }}>
                                                                {item.code}
                                                            </td>
                                                            <td className="p-0" style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: index === 9 ? "none" : "1px solid #000"
                                                            }}>
                                                                <TripleCell {...getCellData(item.code)} />
                                                            </td>
                                                            {index === 0 && (
                                                                <td colSpan={3} rowSpan={10}
                                                                    style={{border: "none"}}></td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </Col>
                                            <Col lg={6} className="p-0">
                                                <table className="w-100 text-center m-0"
                                                       style={{tableLayout: "fixed", borderCollapse: "collapse"}}>
                                                    <colgroup>
                                                        <col style={{width: "4%"}}/>
                                                        <col style={{width: "29%"}}/>
                                                        <col style={{width: "5.5%"}}/>
                                                        <col style={{width: "28%"}}/>
                                                        <col style={{width: "5.5%"}}/>
                                                        <col style={{width: "28%"}}/>
                                                    </colgroup>
                                                    <thead>
                                                    <tr className="align-middle">
                                                        <th rowSpan={2} className="font-weight-bold" style={{
                                                            borderRight: "1px solid #000",
                                                            padding: "9.7px",
                                                            borderBottom: "1px solid #000"
                                                        }}>β
                                                        </th>
                                                        <th colSpan={3} style={{
                                                            fontSize: "0.65rem",
                                                            borderRight: "1px solid #000",
                                                            padding: "9.7px",
                                                            borderBottom: "none"
                                                        }}>ΕΙΣΡΟΕΣ με δικαίωμα έκπτωσης
                                                        </th>
                                                        <th colSpan={2} style={{
                                                            fontSize: "0.65rem",
                                                            padding: "9.7px",
                                                            borderBottom: "none"
                                                        }}>ΦΟΡΟΣ ΕΙΣΡΟΩΝ
                                                        </th>
                                                    </tr>
                                                    <tr style={{borderBottom: "1px solid #000"}}>
                                                        <th colSpan={2} style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}></th>
                                                        <th className="p-0 align-bottom"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleHeader/>
                                                        </th>
                                                        <th style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000"
                                                        }}></th>
                                                        <th className="p-0 align-bottom"
                                                            style={{borderBottom: "1px solid #000"}}>
                                                            <TripleHeader/>
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {[
                                                        {
                                                            c1: 361,
                                                            c2: 381,
                                                            label: "Αγορές & δαπάνες στο εσωτερικό της χώρας"
                                                        },
                                                        {
                                                            c1: 362,
                                                            c2: 382,
                                                            label: "Αγορές & εισαγωγές επενδ. αγαθών (πάγια)"
                                                        },
                                                        {
                                                            c1: 363,
                                                            c2: 383,
                                                            label: "Λοιπές εισαγωγές εκτός επενδ.αγαθών (πάγια)"
                                                        },
                                                        {c1: 364, c2: 384, label: "Ενδοκοινοτικές αποκτήσεις αγαθών"},
                                                        {
                                                            c1: 365,
                                                            c2: 385,
                                                            label: "Ενδοκοινοτικές λήψεις υπηρεσιών άρθρ.14.2.α"
                                                        },
                                                        {c1: 366, c2: 386, label: "Λοιπές πράξεις λήπτη"},
                                                        {c1: 367, c2: 387, label: "ΣΥΝΟΛΟ ΦΟΡΟΛ/ΤΕΩΝ ΕΙΣΡΟΩΝ"},
                                                    ].map(item => (
                                                        <tr key={item.c1}>
                                                            <td colSpan={2} className="align-middle p-0 text-center"
                                                                style={{
                                                                    borderRight: "1px solid #000",
                                                                    borderBottom: "1px solid #000",
                                                                    fontSize: "0.6rem"
                                                                }}>
                                                                {item.label}
                                                            </td>
                                                            <td className="align-middle font-weight-bold p-0" style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000", ...grayBg,
                                                                fontSize: "0.6rem"
                                                            }}>{item.c1}</td>
                                                            <td className="p-0"
                                                                style={{
                                                                    borderRight: "1px solid #000",
                                                                    borderBottom: "1px solid #000"
                                                                }}>
                                                                <TripleCell
                                                                    {...getCellData(item.c1)}
                                                                    code={item.c1}
                                                                    hasActions={true}
                                                                    onActionClick={handleOpenAnalysisModal}
                                                                />
                                                            </td>
                                                            <td className="align-middle font-weight-bold p-0" style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000", ...grayBg,
                                                                fontSize: "0.6rem"
                                                            }}>{item.c2}</td>
                                                            <td className="p-0"
                                                                style={{borderBottom: "1px solid #000"}}>
                                                                <TripleCell
                                                                    {...getCellData(item.c2)}
                                                                    code={item.c2}
                                                                    hasActions={true}
                                                                    onActionClick={handleOpenAnalysisModal}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    <tr>
                                                        <td className="align-middle font-weight-bold" style={{
                                                            padding: "6.5px",
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.7rem"
                                                        }}>δ
                                                        </td>
                                                        <td colSpan={2}
                                                            className="align-middle font-weight-bold p-1 text-center"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000",
                                                                fontSize: "0.6rem"
                                                            }}>
                                                            ΠΡΟΣΤΙΘΕΜΕΝΑ ΠΟΣΑ ΣΤΟ<br/>ΣΥΝΟΛΟ ΤΟΥ ΦΟΡΟΥ ΕΙΣΡΟΩΝ
                                                        </td>
                                                        <td className="p-0 align-bottom" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            borderTop: "1px solid #000"
                                                        }}>
                                                            <TripleHeader hideErp={true} borderTop={true}/>
                                                        </td>
                                                        <td colSpan={2} rowSpan={3} className="align-middle p-0"
                                                            style={{borderBottom: "none"}}>
                                                            <div
                                                                className="d-flex h-100 w-100 align-items-stretch pl-1 pr-2 py-1">
                                                                <div
                                                                    className="d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: "25px",
                                                                        flexShrink: 0,
                                                                        marginRight: "4px"
                                                                    }}>
                                                                    <div style={{
                                                                        marginTop: "40px",
                                                                        backgroundColor: "#000",
                                                                        color: "#fff",
                                                                        writingMode: "vertical-rl",
                                                                        transform: "rotate(180deg)",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "0.4rem",
                                                                        fontWeight: "bold",
                                                                        width: "100%",
                                                                        height: "60px",
                                                                        clipPath: "polygon(100% 0%, 100% 100%, 0% 50%)"
                                                                    }}>ΣΥΝΟΛΟ
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="flex-fill d-flex flex-column align-items-center justify-content-center w-100">
                                                                    <div className="font-weight-bold mt-4" style={{
                                                                        fontSize: "1.2rem",
                                                                        lineHeight: "1",
                                                                        margin: "2px 0"
                                                                    }}>+
                                                                    </div>
                                                                    <div style={{
                                                                        width: "100%",
                                                                        height: "12px",
                                                                        backgroundColor: "#000",
                                                                        clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
                                                                        marginBottom: "3px"
                                                                    }}></div>
                                                                    <div className="d-flex w-100">
                                                                        <div style={{width: "28px"}}></div>
                                                                    </div>
                                                                    <div className="d-flex w-100">
                                                                        <div
                                                                            className="d-flex align-items-center justify-content-center font-weight-bold px-1"
                                                                            style={{
                                                                                width: "28px", ...grayBg,
                                                                                border: "1px solid #000",
                                                                                fontSize: "0.58rem"
                                                                            }}>410
                                                                        </div>
                                                                        <div className="flex-fill"
                                                                             style={{
                                                                                 border: "1px solid #000",
                                                                                 borderLeft: "none"
                                                                             }}>
                                                                            <TripleCell {...getCellData(410)} /></div>
                                                                    </div>
                                                                    <div style={{
                                                                        width: "100%",
                                                                        height: "12px",
                                                                        backgroundColor: "#000",
                                                                        clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)",
                                                                        marginTop: "3px"
                                                                    }}></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>Λοιπά προστιθέμ. ποσά
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>402
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(402)} hideErp={true}/></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>Ποσά διακανονισμών προς<br/>έκπτωση προηγούμενης<br/>διαχειριστικής
                                                            περιόδου
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>407
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(407)} hideErp={true}/></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="align-middle font-weight-bold p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.7rem"
                                                        }}>ε
                                                        </td>
                                                        <td colSpan={3}
                                                            className="align-middle text-center font-weight-bold p-1"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000",
                                                                fontSize: "0.6rem"
                                                            }}>
                                                            ΑΦΑΙΡΟΥΜΕΝΑ ΠΟΣΑ ΑΠΟ ΤΟ<br/>ΣΥΝΟΛΟ ΤΟΥ ΦΟΡΟΥ ΕΙΣΡΟΩΝ
                                                        </td>
                                                        <td colSpan={2} rowSpan={4} className="align-middle p-0"
                                                            style={{borderBottom: "none"}}>
                                                            <div
                                                                className="d-flex h-100 w-100 align-items-stretch pl-1 pr-2 py-1">
                                                                <div
                                                                    className="d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: "25px",
                                                                        flexShrink: 0,
                                                                        marginRight: "4px"
                                                                    }}>
                                                                    <div style={{
                                                                        marginTop: "30px",
                                                                        backgroundColor: "#000",
                                                                        color: "#fff",
                                                                        writingMode: "vertical-rl",
                                                                        transform: "rotate(180deg)",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "0.5rem",
                                                                        fontWeight: "bold",
                                                                        width: "100%",
                                                                        height: "100px",
                                                                        clipPath: "polygon(100% 0%, 100% 100%, 0% 50%)"
                                                                    }}>ΣΥΝΟΛΟ
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="flex-fill d-flex flex-column align-items-center justify-content-center w-100">
                                                                    <div className="font-weight-bold mt-4" style={{
                                                                        fontSize: "1.6rem",
                                                                        lineHeight: "1",
                                                                        margin: "2px 0"
                                                                    }}>-
                                                                    </div>
                                                                    <div style={{
                                                                        width: "100%",
                                                                        height: "12px",
                                                                        backgroundColor: "#000",
                                                                        clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
                                                                        marginBottom: "3px"
                                                                    }}></div>
                                                                    <div className="d-flex w-100">
                                                                        <div
                                                                            className="d-flex align-items-center justify-content-center font-weight-bold"
                                                                            style={{
                                                                                fontSize: "0.6rem",
                                                                                width: "35px", ...grayBg,
                                                                                border: "1px solid #000"
                                                                            }}>428
                                                                        </div>
                                                                        <div className="flex-fill"
                                                                             style={{
                                                                                 border: "1px solid #000",
                                                                                 borderLeft: "none"
                                                                             }}>
                                                                            <TripleCell {...getCellData(428)} /></div>
                                                                    </div>
                                                                    <div style={{
                                                                        width: "100%",
                                                                        height: "12px",
                                                                        backgroundColor: "#000",
                                                                        clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)",
                                                                        marginTop: "3px"
                                                                    }}></div>
                                                                    <div className="font-weight-bold" style={{
                                                                        fontSize: "1.4rem",
                                                                        lineHeight: "1",
                                                                        margin: "2px 0"
                                                                    }}>=
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>ΦΠΑ εισροών που πρέπει<br/>να μειωθεί βάσει ProRata
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>411
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(411)} hideErp={true}/></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000",
                                                            fontSize: "0.6rem"
                                                        }}>Λοιπά<br/>αφαιρούμενα ποσά
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "1px solid #000", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>422
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "1px solid #000"
                                                            }}>
                                                            <TripleCell {...getCellData(422)} hideErp={true}/></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} className="align-middle p-1" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "none",
                                                            fontSize: "0.6rem"
                                                        }}>Ποσά διακανονισμών προς<br/>καταβολή προηγούμενης<br/>διαχειριστικής
                                                            περιόδου
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0" style={{
                                                            borderRight: "1px solid #000",
                                                            borderBottom: "none", ...grayBg,
                                                            fontSize: "0.6rem"
                                                        }}>423
                                                        </td>
                                                        <td className="p-0"
                                                            style={{
                                                                borderRight: "1px solid #000",
                                                                borderBottom: "none"
                                                            }}>
                                                            <TripleCell {...getCellData(423)} hideErp={true}/></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={4} style={{borderTop: borderDark}}></td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={2} style={{borderBottom: "none"}}></td>
                                                        <td colSpan={2}
                                                            className="align-middle text-right pr-2 font-weight-bold"
                                                            style={{
                                                                border: "2px solid #000",
                                                                fontSize: "0.6rem",
                                                                borderRight: "none"
                                                            }}>ΥΠΟΛΟΙΠΟ ΦΟΡΟΥ ΕΙΣΡΟΩΝ
                                                        </td>
                                                        <td className="align-middle font-weight-bold p-0 text-center"
                                                            style={{
                                                                border: "2px solid #000", ...grayBg,
                                                                fontSize: "0.6rem"
                                                            }}>430
                                                        </td>
                                                        <td className="p-0" style={{border: "2px solid #000"}}>
                                                            <TripleCell {...getCellData(430)} /></td>
                                                    </tr>

                                                    </tbody>
                                                </table>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div style={{borderBottom: borderDark}}>
                                        <div className="p-1 d-flex align-items-center"
                                             style={{fontSize: "0.75rem", color: "#000"}}>
                                            <span
                                                className="font-weight-bold mr-2">Γ. ΠΙΝΑΚΑΣ ΕΚΚΑΘΑΡΙΣΗΣ ΤΟΥ ΦΟΡΟΥ</span>
                                            <span className="font-weight-normal">για καταβολή, έκπτωση ή επιστροφή (κωδ. 337 μείον κωδ. 430)</span>
                                        </div>

                                        <table className="w-100 m-0 bg-white" style={{
                                            tableLayout: "fixed",
                                            borderCollapse: "collapse",
                                            borderBottom: "1px solid #000"
                                        }}>
                                            <colgroup>
                                                <col style={{width: "16%"}}/>
                                                <col style={{width: "4%"}}/>
                                                <col style={{width: "19%"}}/>
                                                <col style={{width: "16%"}}/>
                                                <col style={{width: "4%"}}/>
                                                <col style={{width: "19%"}}/>
                                                <col style={{width: "22%"}}/>
                                            </colgroup>
                                            <tbody>
                                            <tr style={{borderBottom: "1px solid #000"}}>
                                                <td colSpan={2} style={{borderRight: "1px solid #000"}}></td>
                                                <td className="p-0 align-bottom"
                                                    style={{borderRight: "1px solid #000"}}>
                                                    <TripleHeader/>
                                                </td>
                                                <td colSpan={2} style={{borderRight: "1px solid #000"}}></td>
                                                <td className="p-0 align-bottom"
                                                    style={{borderRight: "2px solid #000"}}>
                                                    <TripleHeader/>
                                                </td>
                                                <td rowSpan={10} className="align-top p-2" style={{
                                                    borderBottom: "none",
                                                    borderTop: "1px solid #000",
                                                    backgroundColor: "#fff"
                                                }}>
                                        <span className="font-weight-bold d-inline-block mb-1"
                                              style={{fontSize: "0.7rem"}}>
                                            Σημειώσεις:
                                        </span>
                                                    <textarea
                                                        className="w-100 p-1"
                                                        style={{
                                                            fontSize: "0.75rem",
                                                            lineHeight: "1.2rem",
                                                            minHeight: "250px",
                                                            resize: "none",
                                                            outline: "none",
                                                            border: "none"
                                                        }}
                                                        maxLength={200}
                                                        value={f2FormOptions?.notes || ""}
                                                        onChange={(e) => dispatch(setF2FormOptions({notes: e.target.value}))}
                                                        placeholder="Πληκτρολογήστε εδώ τυχόν σημειώσεις..."
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center font-weight-bold p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>ΠΙΣΤΩΤΙΚΟ<br/>ΥΠΟΛΟΙΠΟ
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>470
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleCell {...getCellData(470)} /></td>
                                                <td className="align-middle text-center font-weight-bold p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>ΧΡΕΩΣΤΙΚΟ<br/>ΥΠΟΛΟΙΠΟ
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>480
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleCell {...getCellData(480)} /></td>
                                            </tr>
                                            <tr>
                                                <td colSpan={6} className="align-middle px-2 py-1 font-weight-normal"
                                                    style={{
                                                        fontSize: "0.75rem",
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                    Προσδιορισμός οριστικού ποσού προς καταβολή, έκπτωση ή επιστροφή
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>Πιστωτικό υπόλοιπο<br/>προηγ. φορολ. περιόδου
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>401
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={401} {...getCellData(401)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>Χρεωστικό μέχρι 30€<br/>προηγ. φορολ. περιόδου
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>483
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={483} {...getCellData(483)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>Βεβαιωμένο ποσό κωδ.511 προηγ.<br/>δηλώσεων της ίδιας φορ. περιόδου
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>403
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={403} {...getCellData(403)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>ΠΟΣΟ που σας επιστράφηκε<br/>ή ζητήσατε την επιστροφή του
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>505
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={505} {...getCellData(505)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>Φόρος που έχει δεσμευτεί<br/>μέσω τραπεζών
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>404
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={404} {...getCellData(404)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                                <td colSpan={3} className="p-0"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}></td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center font-weight-bold p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>ΠΟΣΟ<br/>για έκπτωση
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>502
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={502} {...getCellData(502)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                                <td className="align-middle text-center font-weight-bold p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>ΠΟΣΟ**<br/>προς καταβολή
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>511
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleCell {...getCellData(511)} />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center font-weight-bold p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>ΑΙΤΟΥΜΕΝΟ ΠΟΣΟ<br/>για επιστροφή
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>503
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleInputCell code={503} {...getCellData(503)}
                                                                     f2FormOptions={f2FormOptions}
                                                                     onValuesChange={(payload) => dispatch(setF2FormOptions(payload))}/>
                                                </td>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    lineHeight: "1"
                                                }}>Καταβολή ποσού<br/>κωδ. 511 σε 2 δόσεις
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>523
                                                </td>
                                                <td className="p-0 align-middle"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <div
                                                        className="d-flex w-100 h-100 font-weight-bold text-center align-items-stretch"
                                                        style={{fontSize: "0.65rem", minHeight: "35px"}}>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center"
                                                            style={{width: "35%", borderRight: "1px solid #000"}}>
                                                            ΝΑΙ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "15%",
                                                                borderRight: "1px solid #000",
                                                                cursor: "pointer"
                                                            }}
                                                            onClick={() => handleInstallmentChange(1)}>
                                                            <CrossableNumber number={1}
                                                                             isCrossed={f2FormOptions.installment523 === 1}/>
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center"
                                                            style={{width: "35%", borderRight: "1px solid #000"}}>
                                                            ΟΧΙ
                                                        </div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-center"
                                                            style={{width: "15%", cursor: "pointer"}}
                                                            onClick={() => handleInstallmentChange(2)}>
                                                            <CrossableNumber number={2}
                                                                             isCrossed={f2FormOptions.installment523 === 2}/>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle p-1 text-center font-weight-bold" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000",
                                                    letterSpacing: "1px"
                                                }}>ΑΙΤΙΑ ΕΠΙΣΤΡΟΦΗΣ
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>507
                                                </td>
                                                <td colSpan={4} className="align-middle p-0"
                                                    style={{
                                                        borderRight: "2px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <div className="d-flex w-100 h-100 align-items-stretch"
                                                         style={{
                                                             fontSize: "0.6rem",
                                                             minHeight: "30px",
                                                             lineHeight: "1"
                                                         }}>
                                                        <div
                                                            className="d-flex flex-fill align-items-center border-right border-dark"
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => handleReturnReasonChange(1)}>
                                                            <span className="flex-fill text-center px-1">ΑΠΑΛΛ.<br/>ΠΡΑΞΕΙΣ</span>
                                                            <span
                                                                className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                                style={{fontSize: "0.6rem"}}>
                                                    <CrossableNumber number={1}
                                                                     isCrossed={f2FormOptions?.returnReason507 === 1}/>
                                                </span>
                                                        </div>
                                                        <div
                                                            className="d-flex flex-fill align-items-center border-right border-dark"
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => handleReturnReasonChange(2)}>
                                                            <span className="flex-fill text-center px-2">ΠΑΓΙΑ</span>
                                                            <span
                                                                className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                                style={{fontSize: "0.6rem"}}>
                                                    <CrossableNumber number={2}
                                                                     isCrossed={f2FormOptions?.returnReason507 === 2}/>
                                                </span>
                                                        </div>
                                                        <div
                                                            className="d-flex flex-fill align-items-center border-right border-dark"
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => handleReturnReasonChange(3)}>
                                                        <span
                                                            className="flex-fill text-center px-1">ΑΝΑΣΤ.<br/>ΚΑΤΑΒ.</span>
                                                            <span
                                                                className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                                style={{fontSize: "0.6rem"}}>
                                                    <CrossableNumber number={3}
                                                                     isCrossed={f2FormOptions?.returnReason507 === 3}/>
                                                </span>
                                                        </div>
                                                        <div
                                                            className="d-flex flex-fill align-items-center border-right border-dark"
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => handleReturnReasonChange(4)}>
                                                            <span className="flex-fill text-center px-1">ΔΙΑΦΟΡΑ<br/>ΣΥΝΤΕΛ.</span>
                                                            <span
                                                                className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                                style={{fontSize: "0.6rem"}}>
                                                    <CrossableNumber number={4}
                                                                     isCrossed={f2FormOptions?.returnReason507 === 4}/>
                                                </span>
                                                        </div>
                                                        <div
                                                            className="d-flex flex-fill align-items-center border-right border-dark"
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => handleReturnReasonChange(5)}>
                                                        <span
                                                            className="flex-fill text-center px-1">ΑΛΛΗ<br/>ΑΙΤΙΑ</span>
                                                            <span
                                                                className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                                style={{fontSize: "0.6rem"}}>
                                                    <CrossableNumber number={5}
                                                                     isCrossed={f2FormOptions?.returnReason507 === 5}/>
                                                </span>
                                                        </div>
                                                        <div className="d-flex flex-fill align-items-center"
                                                             style={{cursor: "pointer"}}
                                                             onClick={() => handleReturnReasonChange(6)}>
                                                <span
                                                    className="flex-fill text-center px-1">ΜΕΙΩΣΗ<br/>ΧΡΕΩΣΤ. ΥΠΟΛ.</span>
                                                            <span
                                                                className="px-2 border-left border-dark font-weight-bold h-100 d-flex align-items-center bg-light"
                                                                style={{fontSize: "0.6rem"}}>
                                                    <CrossableNumber number={6}
                                                                     isCrossed={f2FormOptions?.returnReason507 === 6}/>
                                                </span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "none",
                                                    lineHeight: "1"
                                                }}>ΑΙΤΗΣΗ ΣΥΜΨΗΦΙΣΜΟΥ του αιτούμενου προς<br/>επιστροφή ποσού με
                                                    μελλοντικές
                                                    οφειλές
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "none"
                                                }}>508
                                                </td>
                                                <td colSpan={4} className="p-0"
                                                    style={{borderRight: "2px solid #000", borderBottom: "none"}}></td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="p-2 border-bottom border-dark bg-white">
                                        <div className="d-flex align-items-center">
                                            <div className="custom-control custom-checkbox mb-0 mr-2">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    id="chkNoIbanAccount"
                                                    checked={Boolean(f2FormOptions?.noIbanAccount)}
                                                    onChange={handleNoIbanAccountChange}
                                                />
                                                <label
                                                    className="custom-control-label"
                                                    htmlFor="chkNoIbanAccount"
                                                    style={{cursor: "pointer"}}
                                                ></label>
                                            </div>
                                            <label
                                                htmlFor="chkNoIbanAccount"
                                                className="mb-0 font-weight-normal"
                                                style={{fontSize: "0.75rem", cursor: "pointer", userSelect: "none"}}
                                            >
                                                Δεν τηρώ λογαριασμό πληρωμών (ΙΒΑΝ) σε πάροχο υπηρεσιών πληρωμών στην
                                                Ελλάδα και σε χώρα εντός του Ενιαίου Χώρου Πληρωμών σε ευρώ (SEPA)
                                            </label>
                                        </div>
                                    </div>
                                    <div style={{borderBottom: borderDark}}>
                                        <table className="w-100 m-0 bg-white" style={{
                                            tableLayout: "fixed",
                                            borderCollapse: "collapse",
                                            borderBottom: "1px solid #000"
                                        }}>
                                            <colgroup>
                                                <col style={{width: "15%"}}/>
                                                <col style={{width: "18%"}}/>
                                                <col style={{width: "5%"}}/>
                                                <col style={{width: "19%"}}/>
                                                <col style={{width: "18%"}}/>
                                                <col style={{width: "5%"}}/>
                                                <col style={{width: "20%"}}/>
                                            </colgroup>
                                            <tbody>
                                            <tr style={{borderBottom: "1px solid #000"}}>
                                                <td rowSpan={3}
                                                    className="align-middle text-center font-weight-bold p-1"
                                                    style={{
                                                        fontSize: "0.7rem",
                                                        borderRight: "1px solid #000",
                                                        letterSpacing: "1px",
                                                        backgroundColor: "#fff"
                                                    }}>
                                                    ΠΛΗΡΟΦΟΡΙΑΚΑ<br/>ΣΤΟΙΧΕΙΑ
                                                </td>
                                                <td colSpan={2} style={{borderRight: "1px solid #000"}}></td>
                                                <td className="p-0 align-bottom"
                                                    style={{borderRight: "1px solid #000"}}>
                                                    <TripleHeader borderTop={false}/>
                                                </td>
                                                <td colSpan={2} style={{borderRight: "1px solid #000"}}></td>
                                                <td className="p-0 align-bottom">
                                                    <TripleHeader borderTop={false}/>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>Πωλήσεις α' κατοικίας
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>906
                                                </td>
                                                <td className="p-0"
                                                    style={{
                                                        borderRight: "1px solid #000",
                                                        borderBottom: "1px solid #000"
                                                    }}>
                                                    <TripleCell {...getCellData(906)} /></td>
                                                <td className="align-middle text-center p-1" style={{
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>Αξία εισροών κωδ.906
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0" style={{
                                                    ...grayBg,
                                                    fontSize: "0.6rem",
                                                    borderRight: "1px solid #000",
                                                    borderBottom: "1px solid #000"
                                                }}>907
                                                </td>
                                                <td className="p-0" style={{borderBottom: "1px solid #000"}}>
                                                    <TripleCell {...getCellData(907)} /></td>
                                            </tr>
                                            <tr>
                                                <td className="align-middle text-center p-1"
                                                    style={{
                                                        fontSize: "0.6rem",
                                                        borderRight: "1px solid #000",
                                                        lineHeight: "1"
                                                    }}>Μικτό
                                                    κέρδος επιχ.<br/>άρθρου 50
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0"
                                                    style={{
                                                        ...grayBg,
                                                        fontSize: "0.6rem",
                                                        borderRight: "1px solid #000"
                                                    }}>908
                                                </td>
                                                <td className="p-0" style={{borderRight: "1px solid #000"}}>
                                                    <TripleCell {...getCellData(908)} /></td>
                                                <td className="align-middle text-center p-1"
                                                    style={{
                                                        fontSize: "0.6rem",
                                                        borderRight: "1px solid #000",
                                                        lineHeight: "1"
                                                    }}>Αξία
                                                    απαλλασσόμενων<br/>πράξεων από και προς την<br/>περιοχή του Αγίου
                                                    Όρους
                                                </td>
                                                <td className="font-weight-bold text-center align-middle p-0"
                                                    style={{
                                                        ...grayBg,
                                                        fontSize: "0.6rem",
                                                        borderRight: "1px solid #000"
                                                    }}>912
                                                </td>
                                                <td className="p-0"><TripleCell {...getCellData(912)} /></td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="bg-white">
                                        <div className="p-2 pt-2 d-flex justify-content-between align-items-end"
                                             style={{fontSize: "0.6rem"}}>
                                            <div>
                                                Συμπληρώνεται από την Υπηρεσία.<br/>
                                                Αν οποιοδήποτε ποσό που καταχωρείται είναι αρνητικός αριθμός,
                                                σημειώστε το
                                                πρόσημο
                                                πλήν (-) πριν απ' αυτό.<br/>
                                                Τα ποσά σε ΕΥΡΩ αναγράφονται <strong>υποχρεωτικά</strong> με τη χρήση
                                                δύο
                                                (2)
                                                δεκαδικών ψηφίων μετά την υποδιαστολή, π.χ. 45,00 ή 45,63.<br/>
                                                ** Χρεωστικό υπόλοιπο μέχρι τριάντα (30) ευρώ μεταφέρεται για καταβολή
                                                στην
                                                επόμενη
                                                φορολογική περίοδο, με εξαίρεση την παύση εργασιών φορολογητέας
                                                δραστηριότητας.
                                            </div>
                                            <div className="text-right font-weight-bold"
                                                 style={{whiteSpace: "nowrap", fontSize: "0.6rem"}}>
                                                ΕΚΔΟΣΗ 2025 &nbsp;&nbsp;&nbsp;&nbsp; 050 - Φ.Π.Α.<br/>
                                                ΑΑΔΕ<br/>
                                                Γ.Γ.Π.Σ.Ψ.Δ. - Δ.Λ.Ο.Σ.Ε.Λ.Υ.Υ.Χ.
                                            </div>
                                        </div>
                                    </div>
                                </F2ColumnsContext.Provider>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {responseData && (
                <div
                    className="w-100 position-sticky bg-white border-top py-1 px-2 d-flex justify-content-between align-items-center print-hide mt-auto"
                    style={{
                        bottom: 0,
                        zIndex: 3,
                        boxShadow: "0 -4px 10px -2px rgba(0,0,0,0.1)"
                    }}
                >
                    <div className="d-flex align-items-center px-1 py-1 mx-1">
                        <span className="mr-2 text-dark font-weight-bold"
                              style={{fontSize: "0.7rem", fontWeight: "600"}}>
                            Zoom:
                        </span>
                        <Button
                            variant="light"
                            size="sm"
                            className="py-1 px-2 border font-weight-bold"
                            onClick={() => dispatch(setF2ZoomLevel(Math.max(zoomLevel - 0.1, 0.4)))}
                        >
                            -
                        </Button>
                        <span className="mx-2 font-weight-bold text-primary"
                              style={{minWidth: "35px", textAlign: "center", fontSize: "0.75rem"}}>
                                    {Math.round(zoomLevel * 100)}%
                        </span>
                        <Button
                            variant="light"
                            size="sm"
                            className="py-1 px-2 border font-weight-bold"
                            onClick={() => dispatch(setF2ZoomLevel(Math.min(zoomLevel + 0.1, 2.0)))}
                        >
                            +
                        </Button>
                        {zoomLevel !== 1 && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="py-1 px-1 ml-2 border"
                                onClick={() => dispatch(setF2ZoomLevel(1))}
                                title="Επαναφορά στο 100%"
                            >
                                <i className="fa-solid fa-arrow-rotate-left"></i>
                            </Button>
                        )}
                        <div className="d-flex align-items-center ml-4 pl-3 border-left border-secondary">
                            <span className="mr-2 text-dark font-weight-bold"
                                  style={{fontSize: "0.7rem", fontWeight: "600"}}>
                                Προβολή:
                            </span>
                            <div className="custom-control custom-checkbox custom-control-inline mb-0 mr-2">
                                <input type="checkbox" className="custom-control-input" id="chkColErp"
                                       checked={f2VisibleColumns.erp}
                                       onChange={() => handleToggleColumn('erp')}/>
                                <label className="custom-control-label font-weight-bold" htmlFor="chkColErp" style={{
                                    fontSize: "0.65rem",
                                    paddingTop: "5px",
                                    cursor: "pointer"
                                }}>ΕΜΠΟΡΙΚΟ</label>
                            </div>
                            <div className="custom-control custom-checkbox custom-control-inline mb-0 mr-2">
                                <input type="checkbox" className="custom-control-input" id="chkColMyData"
                                       checked={f2VisibleColumns.myData}
                                       onChange={() => handleToggleColumn('myData')}/>
                                <label className="custom-control-label text-primary font-weight-bold"
                                       htmlFor="chkColMyData" style={{
                                    fontSize: "0.65rem",
                                    paddingTop: "5px",
                                    cursor: "pointer"
                                }}>MYDATA</label>
                            </div>
                            <div className="custom-control custom-checkbox custom-control-inline mb-0">
                                <input type="checkbox" className="custom-control-input" id="chkColAcc"
                                       checked={f2VisibleColumns.accounting}
                                       onChange={() => handleToggleColumn('accounting')}/>
                                <label className="custom-control-label font-weight-bold" htmlFor="chkColAcc" style={{
                                    fontSize: "0.65rem",
                                    paddingTop: "5px",
                                    cursor: "pointer"
                                }}>ΛΟΓΙΣΤΙΚΗ</label>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="mr-3 text-dark font-weight-bold">Έκδοση Εντύπου Φ2:</span>
                        <BootstrapDropdown
                            drop="up"
                            onSelect={(eventKey) => dispatch(setF2ExportSource(eventKey))}
                            className="mr-2"
                        >
                            <BootstrapDropdown.Toggle variant="primary" size="sm">
                                {exportSource === "erp" ? "Εμπορικό" : exportSource === "myData" ? "MyData" : "Λογιστική"}
                            </BootstrapDropdown.Toggle>
                            <BootstrapDropdown.Menu>
                                <BootstrapDropdown.Item eventKey="erp"
                                                        active={exportSource === "erp"}>Εμπορικό</BootstrapDropdown.Item>
                                <BootstrapDropdown.Item eventKey="myData"
                                                        active={exportSource === "myData"}>MyData</BootstrapDropdown.Item>
                                <BootstrapDropdown.Item eventKey="accounting"
                                                        active={exportSource === "accounting"}>Λογιστική</BootstrapDropdown.Item>
                            </BootstrapDropdown.Menu>
                        </BootstrapDropdown>

                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleTriggerExtension('FORM')}
                            disabled={!responseData || taxisLoading}
                            title={!responseData ? "Απαιτείται πρώτα Υπολογισμός Φ2" : !isExtensionInstalled ? "Απαιτείται εγκατάσταση του πρόσθετου" : "Αυτόματη συμπλήρωση στο Taxis"}
                        >
                            {taxisLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2"/>
                                    Σύνδεση με Taxis...
                                </>
                            ) : (
                                <>
                                    <i className={`fa-solid fa-cloud-arrow-up fa-sm mr-2`}></i>
                                    Συμπλήρωση στο Taxis
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            <Modal
                show={showAnalysisModal}
                onHide={handleCloseAnalysisModal}
                size="md"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h4>Ανάλυση ανά κατηγορία
                            ΦΠΑ {activeAnalysisData.code ? `- Κωδικός ${activeAnalysisData.code}` : ""}</h4>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activeAnalysisData.source === "accounting" ? (
                        <div className="text-center py-5">
                            <h5>Ανάλυση Λογιστικής</h5>
                        </div>
                    ) : modalLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary"/>
                            <div className="mt-2 text-muted">Άντληση δεδομένων...</div>
                        </div>
                    ) : (
                        <React.Fragment>
                            {modalData && modalData[activeAnalysisData.baseCode] && Object.keys(modalData[activeAnalysisData.baseCode]).length > 0 ? (
                                (() => {
                                    const isNetValue = activeAnalysisData.code === activeAnalysisData.baseCode;
                                    const isVatValue = activeAnalysisData.code !== activeAnalysisData.baseCode;

                                    return (
                                        <table className="simpleClassicTable simpleClassicTableHoverable w-100">
                                            <thead>
                                            <tr>
                                                <th className="text-center px-2">Κατηγορία ΦΠΑ</th>
                                                {isNetValue && (
                                                    <th className="text-right px-2">Καθαρή Αξία</th>
                                                )}
                                                {isVatValue && (
                                                    <th className="text-right px-2">Αξία ΦΠΑ</th>
                                                )}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {Object.keys(modalData[activeAnalysisData.baseCode])
                                                .sort((a, b) => Number(b) - Number(a))
                                                .map((pctKey) => {
                                                    const values = modalData[activeAnalysisData.baseCode][pctKey];
                                                    if (isNetValue && values.net === 0) return null;
                                                    if (isVatValue && values.vat === 0) return null;

                                                    return (
                                                        <tr key={pctKey}>
                                                            <td className="text-center align-middle font-weight-bold">
                                                                {pctKey}%
                                                            </td>
                                                            {isNetValue && (
                                                                <td className="text-right align-middle">
                                                                    {getGreekCurrencyFormat(values.net)}
                                                                </td>
                                                            )}
                                                            {isVatValue && (
                                                                <td className="text-right align-middle">
                                                                    {getGreekCurrencyFormat(values.vat)}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    );
                                })()
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    Δεν βρέθηκαν κινήσεις Εμπορικού (ERP) για τον κωδικό {activeAnalysisData.code} στη
                                    συγκεκριμένη περίοδο.
                                </div>
                            )}
                        </React.Fragment>
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                show={showExtensionModal}
                onHide={() => setShowExtensionModal(false)}
                dialogClassName={"modal65PercentWidth"}
                centered
            >
                <Modal.Header closeButton className="border-bottom-0 pb-2">
                    <Modal.Title className="d-flex align-items-center">
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{fontSize: "1.05rem", color: "#111827"}}>
                                Απαιτείται το Πρόσθετο Primer ERP - Taxis Bridge
                            </h5>
                            <span className="text-muted" style={{fontSize: "0.75rem"}}>
                                Αυτοματοποίηση διασύνδεσης με το σύστημα Taxisnet
                            </span>
                        </div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-2 pb-3 px-4">
                    <p className="text-muted mb-3" style={{fontSize: "0.85rem", lineHeight: "1.45"}}>
                        Για την ασφαλή άντληση των δηλώσεων ΦΠΑ και την αυτόματη επικοινωνία με την ΑΑΔΕ, είναι
                        απαραίτητη η εγκατάσταση και σωστή ρύθμιση του εργαλείου στον περιηγητή σας.
                    </p>

                    <div
                        className="p-3 mb-3 rounded"
                        style={{
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0"
                        }}
                    >
                        <div className="font-weight-bold mb-3 text-dark" style={{fontSize: "0.8rem"}}>
                            Οδηγίες Εγκατάστασης:
                        </div>

                        <div className="d-flex flex-column" style={{gap: "10px"}}>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    1
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Πατήστε το κουμπί <strong>«Μετάβαση στο Chrome Web Store»</strong> παρακάτω.
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    2
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Στη σελίδα που θα ανοίξει, πατήστε <strong>«Προσθήκη στο Chrome»</strong> (Add to Chrome).
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    3
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Ανοίξτε τη διαχείριση επεκτάσεων (πληκτρολογήστε <code
                                    className="px-1 py-0.5 bg-light rounded border text-danger">chrome://extensions</code> στη γραμμή διευθύνσεων ή πατήστε το εικονίδιο παζλ πάνω δεξιά).
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    4
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Εντοπίστε το <strong>Primer ERP - Taxis Bridge</strong> και πατήστε <strong>Λεπτομέρειες (Details)</strong>.
                                </span>
                            </div>
                            <div className="d-flex align-items-start">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2 text-white bg-primary flex-shrink-0"
                                    style={{width: "20px", height: "20px", fontSize: "0.7rem", marginTop: "1px"}}
                                >
                                    5
                                </span>
                                <span className="text-dark" style={{fontSize: "0.8rem", lineHeight: "1.45"}}>
                                    Ενεργοποιήστε την επιλογή <strong>«Να επιτρέπεται σε ανώνυμη περιήγηση» (Allow in incognito)</strong>.
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center text-muted" style={{fontSize: "0.75rem"}}>
                        <i className="fa-solid fa-shield-halved text-primary mr-2"></i>
                        <span>Ασφαλές εργαλείο, εγκεκριμένο από το επίσημο Chrome Web Store.</span>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-top-0 pt-0 px-4 pb-3">
                    <a
                        href={EXTENSION_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center"
                        onClick={() => setShowExtensionModal(false)}
                    >
                        <i className="fa-brands fa-chrome mr-2"></i>
                        Μετάβαση στο Chrome Web Store
                    </a>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
};

export default AccountingIssueVatInformation;